import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../../shared/storage/firebase';
import { useAuth } from '../../auth/contexts/AuthContext';
import type { BillingProfile, Invoice, InvoiceLineItem, InvoiceTemplate, InvoiceAccessToken, InvoicePaymentSubmission, School, SchoolContactWithId, Squad } from '../../../types';
import { programTermForDate, programTermLabelFromId } from '../../../shared/terms/programTerms';
import { deriveInvoiceStatus, daysUntil } from '../../../shared/billing/invoiceStatus';
import { buildInvoiceLineItemsFromSquads, computeServiceRangeFromSquads, resolveSquadTermId } from '../../../shared/billing/squadInvoiceRollup';
import '../styles/AdminBillingPage.css';
import CustomInvoiceClasses from './CustomInvoiceClasses';

function SearchableSelect(props: {
  valueId: string;
  query: string;
  placeholder?: string;
  options: Array<{ id: string; label: string }>;
  disabled?: boolean;
  onQueryChange: (q: string) => void;
  onSelectId: (id: string) => void;
  onResetQueryToValue?: () => void;
}): React.ReactElement {
  const { valueId, query, placeholder, options, disabled, onQueryChange, onSelectId, onResetQueryToValue } = props;
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuIdRef = useRef(`billing-searchable-menu-${Math.random().toString(36).slice(2)}`);

  const filtered = useMemo(() => {
    const needle = String(query || '').trim().toLowerCase();
    if (!needle) return options;
    return options.filter(o => o.label.toLowerCase().includes(needle) || o.id.toLowerCase().includes(needle));
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const root = rootRef.current;
      if (!root) return;
      const t = e.target as any;
      if (t && root.contains(t)) return;
      setOpen(false);
      setActiveIdx(-1);
      onResetQueryToValue?.();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, onResetQueryToValue]);

  const commitSelect = (id: string) => {
    onSelectId(id);
    setOpen(false);
    setActiveIdx(-1);
  };

  return (
    <div className={`billing-searchable-select ${disabled ? 'is-disabled' : ''}`} ref={rootRef}>
      <input
        value={query}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => {
          onQueryChange(e.target.value);
          setOpen(true);
          setActiveIdx(-1);
        }}
        onFocus={() => setOpen(true)}
        onBlur={(e) => {
          const next = (e.relatedTarget as any) || null;
          const root = rootRef.current;
          if (root && next && root.contains(next)) return;
          setOpen(false);
          setActiveIdx(-1);
          onResetQueryToValue?.();
        }}
        onKeyDown={(e) => {
          if (disabled) return;
          if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            setOpen(true);
            return;
          }
          if (e.key === 'Escape') {
            setOpen(false);
            setActiveIdx(-1);
            onResetQueryToValue?.();
            return;
          }
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setActiveIdx(prev => Math.min(filtered.length - 1, (prev < 0 ? 0 : prev + 1)));
            return;
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIdx(prev => Math.max(0, (prev < 0 ? 0 : prev - 1)));
            return;
          }
          if (e.key === 'Enter') {
            if (!open) return;
            e.preventDefault();
            const idx = activeIdx >= 0 ? activeIdx : 0;
            const opt = filtered[idx];
            if (opt) commitSelect(opt.id);
          }
        }}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={menuIdRef.current}
        aria-autocomplete="list"
      />

      <span className="billing-searchable-caret" aria-hidden="true">▾</span>

      {open && filtered.length > 0 && (
        <div id={menuIdRef.current} className="billing-searchable-menu" role="listbox">
          {filtered.map((opt, idx) => {
            const isActive = idx === activeIdx;
            const isSelected = String(valueId || '') === String(opt.id || '');
            return (
              <button
                key={opt.id}
                type="button"
                className={`billing-searchable-item ${isActive ? 'is-active' : ''} ${isSelected ? 'is-selected' : ''}`}
                onMouseEnter={() => setActiveIdx(idx)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commitSelect(opt.id)}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const DEFAULT_TEMPLATE: Partial<InvoiceTemplate> = {
  title: 'INVOICE',
  overtureName: 'Overture',
  overtureAddress: 'Chicago, IL',
  paymentInstructions:
    'Payment instructions:\n'
    + '- We prefer ACH. (details provided separately)\n'
    + '- Check: payable to Overture (include invoice # in memo)\n',
};

function cents(n: number): number {
  return Math.round(Number(n || 0));
}

function computeLineItemAmount(li: InvoiceLineItem): number {
  const claimedClassCount = Array.isArray((li as any).classDates) ? (li as any).classDates.length : 0;
  if (((li as any).source === 'computed' || (li as any).customSessionPricing) && claimedClassCount > 0) {
    const students = Math.max(0, Math.round(Number(((li as any).billedHeadcount ?? (li as any).quantity) || 0)));
    return cents((li as any).rateCents) * claimedClassCount * ((li as any).paymentMode === 'school_enrollment' ? students : 1);
  }
  // Only squad-derived flat-fee lines should force quantity = 1.
  // Manual lines may still choose a "type" for display/PDF but should keep editable quantity.
  const isDerivedFlatFee =
    (li as any).paymentMode === 'school_flat_fee' &&
    (li as any).manual !== true &&
    Boolean(String((li as any).squadId || '').trim());
  const qty = isDerivedFlatFee ? 1 : Math.max(0, Math.round(Number((li as any).quantity || 0)));
  return cents((li as any).rateCents) * qty;
}

function safeIsoDate(v: string): string {
  // Expect YYYY-MM-DD from <input type="date">
  return String(v || '').trim();
}

function addDaysYmd(ymd: string, days: number): string {
  const base = String(ymd || '').trim();
  const n = Number(days);
  if (!base) return '';
  const m = base.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return base;
  const d = new Date(`${base}T00:00:00`);
  if (Number.isNaN(d.getTime())) return base;
  const delta = Number.isFinite(n) ? Math.trunc(n) : 0;
  d.setDate(d.getDate() + delta);
  return d.toISOString().slice(0, 10);
}

function billingTodayYmd(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  const value = Object.fromEntries(parts.map(part => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
}

function stripUndefinedDeep<T>(v: T): T {
  // Firestore rejects undefined values anywhere in the payload.
  // This removes undefined keys recursively while preserving other values (including null).
  if (v === undefined) return v;
  if (v === null) return v;
  if (Array.isArray(v)) {
    return (v.map(x => stripUndefinedDeep(x)).filter(x => x !== undefined) as any) as T;
  }
  if (typeof v === 'object') {
    const out: any = {};
    for (const [k, val] of Object.entries(v as any)) {
      const cleaned = stripUndefinedDeep(val);
      if (cleaned !== undefined) out[k] = cleaned;
    }
    return out as T;
  }
  return v;
}

function generateInternalInvoiceNumber(params: { invoiceDate: string; schoolName: string; termId: string }): string {
  const d = safeIsoDate(params.invoiceDate) || new Date().toISOString().slice(0, 10);
  const slug = String(params.schoolName || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 24) || 'school';
  return `INV-${d.replace(/-/g, '')}-${slug}-${params.termId}`;
}

function fmtTimestamp(v: any): string {
  if (!v) return '—';
  try {
    if (typeof v?.toDate === 'function') {
      const d = v.toDate();
      if (d instanceof Date && !Number.isNaN(d.getTime())) return d.toLocaleString();
    }
  } catch (_) {}
  return '—';
}

function fmtUsd(centsValue: any): string {
  const n = Number(centsValue);
  if (!Number.isFinite(n)) return '—';
  return `$${(n / 100).toFixed(2)}`;
}

function fmtYmdLong(ymd: any): string {
  const s = String(ymd || '').trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s || '—';
  const d = new Date(`${s}T00:00:00`);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtYmdMdY(ymd: any): string {
  const s = String(ymd || '').trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[2]}/${m[3]}/${m[1]}`;
  return s || '—';
}

function fmtDateLong(v: any): string {
  if (!v) return '—';
  // YYYY-MM-DD
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v.trim())) return fmtYmdLong(v);
  // Firestore Timestamp
  try {
    if (typeof v?.toDate === 'function') {
      const d = v.toDate();
      if (d instanceof Date && !Number.isNaN(d.getTime())) {
        return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
      }
    }
  } catch (_) {}
  // Date
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    return v.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }
  return '—';
}

function dayDelta(a: any, b: any): number | null {
  // returns a - b in whole days (rounded)
  const toMs = (x: any): number | null => {
    if (!x) return null;
    if (typeof x === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(x.trim())) {
      const t = Date.parse(`${x.trim()}T00:00:00`);
      return Number.isFinite(t) ? t : null;
    }
    if (typeof x?.toDate === 'function') {
      const d = x.toDate();
      const t = d instanceof Date ? d.getTime() : NaN;
      return Number.isFinite(t) ? t : null;
    }
    if (x instanceof Date && Number.isFinite(x.getTime())) return x.getTime();
    return null;
  };
  const am = toMs(a);
  const bm = toMs(b);
  if (am == null || bm == null) return null;
  return Math.round((am - bm) / (24 * 60 * 60 * 1000));
}

function statusLabel(s: string): string {
  switch (s) {
    case 'needs_pdf': return 'Needs PDF';
    case 'ready_to_send': return 'Ready to send';
    case 'late_draft': return 'Late draft';
    case 'overdue': return 'Overdue';
    case 'paid': return 'Paid';
    case 'sent': return 'Sent';
    case 'canceled': return 'Canceled';
    case 'draft':
    default: return 'Draft';
  }
}

function joinWithDots(parts: Array<React.ReactNode | null | undefined>): React.ReactNode {
  const kept = parts.filter(Boolean) as React.ReactNode[];
  if (kept.length === 0) return null;
  const out: React.ReactNode[] = [];
  kept.forEach((p, i) => {
    if (i > 0) out.push(<span key={`dot-${i}`} className="status-dot"> • </span>);
    out.push(<span key={`part-${i}`}>{p}</span>);
  });
  return <>{out}</>;
}

export default function AdminBillingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { schoolId: schoolIdParam, invoiceId: invoiceIdParam } = useParams() as any;
  const [searchParams, setSearchParams] = useSearchParams();
  const { userData } = useAuth();

  const schoolIdFromRoute = String(schoolIdParam || '').trim();
  const invoiceIdFromRoute = String(invoiceIdParam || '').trim();
  const invoiceIdFromQuery = String(searchParams.get('invoiceId') || '').trim();
  const explicitInvoiceId = invoiceIdFromRoute || invoiceIdFromQuery;

  const [resolvedSchoolId, setResolvedSchoolId] = useState<string>(schoolIdFromRoute);
  const [resolvedInvoiceId, setResolvedInvoiceId] = useState<string>(explicitInvoiceId);
  const [resolvedPartnerId, setResolvedPartnerId] = useState<string>('');
  const [resolvedLocationId, setResolvedLocationId] = useState<string>('');

  const termIdFromQuery = String(searchParams.get('termId') || '').trim();

  const defaultTermId = useMemo(() => programTermForDate(new Date()).id, []);
  const [termId, setTermId] = useState<string>(termIdFromQuery || defaultTermId);

  // Keep route/query changes in sync with local state
  useEffect(() => {
    setResolvedSchoolId(schoolIdFromRoute);
  }, [schoolIdFromRoute]);
  useEffect(() => {
    setResolvedInvoiceId(explicitInvoiceId);
  }, [explicitInvoiceId]);

  const [school, setSchool] = useState<School | null>(null);
  const [squads, setSquads] = useState<Squad[]>([]);
  const [selectedSquadIds, setSelectedSquadIds] = useState<string[]>([]);
  const [squadSearch, setSquadSearch] = useState<string>('');
  const [autoSaveFailedKey, setAutoSaveFailedKey] = useState<string | null>(null);
  const autoSaveTimerRef = useRef<number | null>(null);

  const [billingProfile, setBillingProfile] = useState<BillingProfile>({});
  const [billingProfileBaseline, setBillingProfileBaseline] = useState<BillingProfile>({});
  const [schoolContacts, setSchoolContacts] = useState<SchoolContactWithId[]>([]);

  const [authorizedContactQuery, setAuthorizedContactQuery] = useState('');
  const [authorizedContactPickId, setAuthorizedContactPickId] = useState('');

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [invoiceBaseline, setInvoiceBaseline] = useState<Invoice | null>(null);
  const [invoiceTemplate, setInvoiceTemplate] = useState<InvoiceTemplate | null>(null);
  const [portalTokens, setPortalTokens] = useState<InvoiceAccessToken[]>([]);
  const [paymentSubmissions, setPaymentSubmissions] = useState<InvoicePaymentSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [sendPreviewOpen, setSendPreviewOpen] = useState<null | 'send' | 'resend'>(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfGenStartedAtMs, setPdfGenStartedAtMs] = useState<number | null>(null);
  const [pdfGenTick, setPdfGenTick] = useState(0);
  const [sendPreviewPdfUrlAtOpen, setSendPreviewPdfUrlAtOpen] = useState<string | null>(null);
  const [sendPreviewError, setSendPreviewError] = useState<string | null>(null);
  const [sendActionError, setSendActionError] = useState<string | null>(null);
  const [coverageDirty, setCoverageDirty] = useState(false);
  const invoiceDateInputRef = useRef<HTMLInputElement | null>(null);
  const dueDateInputRef = useRef<HTMLInputElement | null>(null);
  const [confirmModal, setConfirmModal] = useState<null | {
    title: string;
    body: React.ReactNode;
    confirmText?: string;
    confirmTone?: 'default' | 'danger';
    onConfirm: () => void | Promise<void>;
  }>(null);
  const [createAuthorizedContactModalOpen, setCreateAuthorizedContactModalOpen] = useState(false);
  const [createAuthorizedContactDraft, setCreateAuthorizedContactDraft] = useState<{ displayName: string; email: string }>({
    displayName: '',
    email: '',
  });
  const [termsAccordionOpen, setTermsAccordionOpen] = useState(false);
  const [enrollmentWarningsAcknowledged, setEnrollmentWarningsAcknowledged] = useState(false);

  const derivedStatus = useMemo(() => deriveInvoiceStatus(invoice), [invoice]);
  const isSent = Boolean((invoice as any)?.sentAt);
  const isCanceled = derivedStatus === 'canceled';
  const isReadOnlyRecord = isSent || isCanceled;
  const statusTone = useMemo(() => {
    if (derivedStatus === 'paid') return 'ok';
    if (derivedStatus === 'overdue' || derivedStatus === 'late_draft') return 'danger';
    if (derivedStatus === 'ready_to_send') return 'warn';
    if (derivedStatus === 'sent') return 'info';
    return 'neutral';
  }, [derivedStatus]);

  const statusSentence = useMemo((): React.ReactNode => {
    const send = invoice?.invoiceSendDate || invoice?.invoiceDate || null;
    const due = invoice?.paymentDueDate || null;
    const sentAt = (invoice as any)?.sentAt || null;
    const paidAt = (invoice as any)?.paidAt || null;
    const dueDays = daysUntil(due);
    const todayYmd = new Date().toISOString().slice(0, 10);
    const scheduledIsToday = typeof send === 'string' && send.slice(0, 10) === todayYmd;
    const dueCountdown =
      dueDays != null
        ? dueDays >= 0
          ? `(${dueDays} days)`
          : `(${Math.abs(dueDays)} days past due)`
        : null;

    if (derivedStatus === 'paid') {
      const paidAtText = paidAt ? fmtTimestamp(paidAt) : '—';
      return joinWithDots(['Paid', paidAtText]);
    }
    if (derivedStatus === 'canceled') {
      return (invoice as any)?.replacedByInvoiceId ? 'Canceled • Replacement created' : 'Canceled • No payment is requested';
    }
    if (derivedStatus === 'overdue' || derivedStatus === 'late_draft') {
      const past = dueDays != null ? Math.abs(Math.min(0, dueDays)) : null;
      return joinWithDots([
        `Payment due ${fmtYmdLong(due)}`,
        past != null ? `${past} days past due` : null,
      ]);
    }
    if (derivedStatus === 'sent') {
      const planned = invoice?.invoiceSendDate || invoice?.invoiceDate || null;
      const actual = sentAt || planned;
      const delta = planned ? dayDelta(actual, planned) : null;
      const sentWhen =
        delta == null || delta === 0
          ? `Sent ${fmtDateLong(actual)}`
          : delta < 0
            ? `Sent early on ${fmtDateLong(actual)}`
            : `Sent late on ${fmtDateLong(actual)}`;
      const plannedPart = planned ? `Invoice planned ${fmtDateLong(planned)}` : null;
      const duePart = `Payment due ${fmtYmdLong(due)}`;
      return joinWithDots([sentWhen, plannedPart, duePart, dueCountdown]);
    }
    if (derivedStatus === 'ready_to_send') {
      return joinWithDots([
        `Due ${fmtYmdLong(due)}${dueCountdown ? ` ${dueCountdown}` : ''}`,
        !scheduledIsToday ? `Scheduled ${fmtYmdLong(send)}` : null,
      ]);
    }
    if (derivedStatus === 'needs_pdf') {
      return 'Upload or generate a PDF to send this invoice.';
    }
    return joinWithDots([
      !scheduledIsToday ? `Scheduled ${fmtYmdLong(send)}` : null,
      `Due ${fmtYmdLong(due)}${dueCountdown ? ` ${dueCountdown}` : ''}`,
    ]);
  }, [derivedStatus, invoice]);

  // Keep query param in sync for deep links
  useEffect(() => {
    if (!termId) return;
    const next = new URLSearchParams(searchParams);
    next.set('termId', termId);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termId]);

  const billingProfileRef = useMemo(() => {
    const pid = String(resolvedPartnerId || '').trim();
    return doc(db, 'partners', pid || '__unmapped_read_only__');
  }, [resolvedPartnerId]);

  const writeBillingProfile = useCallback(async (profile: BillingProfile): Promise<void> => {
    if (!resolvedPartnerId) {
      throw new Error('Historical school billing is read-only. Map this invoice/location to a partner before editing billing.');
    }
    await setDoc(billingProfileRef, {
      billingDefaults: profile,
      updatedAt: Timestamp.now(),
      updatedBy: userData?.id || '',
    } as any, { merge: true });
  }, [billingProfileRef, resolvedPartnerId, userData?.id]);

  const termsKey = useCallback((p: BillingProfile) => {
    const addr = (p as any)?.billingAddress || {};
    return JSON.stringify({
      legalName: String((p as any)?.legalName || ''),
      paymentPreference: String((p as any)?.paymentPreference || ''),
      netInvoiceDays: Number((p as any)?.netInvoiceDays ?? 0),
      netPaymentDays: Number((p as any)?.netPaymentDays ?? 0),
      billingContactId: String((p as any)?.billingContactId || ''),
      billingAddress: {
        line1: String(addr?.line1 || ''),
        line2: String(addr?.line2 || ''),
        city: String(addr?.city || ''),
        state: String(addr?.state || ''),
        postalCode: String(addr?.postalCode || ''),
        country: String(addr?.country || ''),
      },
    });
  }, []);

  const termsDirty = useMemo(() => termsKey(billingProfile) !== termsKey(billingProfileBaseline), [billingProfile, billingProfileBaseline, termsKey]);

  const contactById = useMemo(() => {
    const m = new Map<string, SchoolContactWithId>();
    schoolContacts.forEach(c => {
      m.set(String(c.id), c);
      const legacyIds = Array.isArray((c as any)?.legacyContactIds) ? (c as any).legacyContactIds : [];
      legacyIds.forEach((id: unknown) => {
        const cleanId = String(id || '').trim();
        if (cleanId) m.set(cleanId, c);
      });
    });
    return m;
  }, [schoolContacts]);

  const authorizedContactIds = useMemo(
    () => (Array.isArray((billingProfile as any)?.authorizedContactIds) ? ((billingProfile as any).authorizedContactIds as any[]) : [])
      .map((x: any) => String(x || '').trim())
      .filter(Boolean),
    [billingProfile]
  );

  const authorizedContacts = useMemo(
    () => authorizedContactIds.map(id => contactById.get(id)).filter(Boolean) as SchoolContactWithId[],
    [authorizedContactIds, contactById]
  );

  const billingContactId = useMemo(() => String((billingProfile as any)?.billingContactId || '').trim(), [billingProfile]);
  const billingContact = useMemo(() => {
    const primary = billingContactId ? authorizedContacts.find(c => String(c.id) === billingContactId) : null;
    if (primary) return primary;
    // Fallback: first authorized contact with an email
    const firstWithEmail = authorizedContacts.find(c => String((c as any).email || '').trim());
    return firstWithEmail || null;
  }, [authorizedContacts, billingContactId]);

  const authorizedEmails = useMemo(
    () => Array.from(new Set(authorizedContacts.map(c => String((c as any).email || '').trim()).filter(Boolean))),
    [authorizedContacts]
  );

  const toEmail = useMemo(() => String((billingContact as any)?.email || '').trim(), [billingContact]);
  const ccAuthorizedEmails = useMemo(
    () => authorizedEmails.filter(e => e && e.toLowerCase() !== toEmail.toLowerCase()),
    [authorizedEmails, toEmail]
  );

  const resendEmailPreview = useMemo(() => {
    const inv: any = invoice as any;
    if (!inv) return null;
    const schoolName = String(inv?.schoolNameSnapshot || (school as any)?.name || (school as any)?.displayName || 'your school').trim();
    const termLabel = String(inv?.schoolTermSnapshot || termId).trim();
    const baseSubject = `Invoice Overture ${schoolName || 'your school'} — ${termLabel}`;
    const subject = `Quick follow-up: ${baseSubject}`;

    const sentAtDate: Date | null = (() => {
      try {
        if (typeof inv?.sentAt?.toDate === 'function') {
          const d = inv.sentAt.toDate();
          if (d instanceof Date && !Number.isNaN(d.getTime())) return d;
        }
      } catch (_) {}
      return null;
    })();
    const sentLabel = sentAtDate
      ? sentAtDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
      : '—';
    const paymentDueLabel = fmtYmdMdY(inv?.paymentDueDate);
    const configuredSignature = String((invoiceTemplate as any)?.signatureName || '').trim();
    const signatureName = configuredSignature && !/^aspen\b/i.test(configuredSignature) ? configuredSignature : 'Steven';

    const body = [
      'Hi there,',
      '',
      `Just wanted to follow up on the invoice I sent on ${sentLabel} — wanted to make sure it didn’t get lost in the shuffle!`,
      '',
      "I've attached everything again for your convenience: the invoice, our W-9, and ACH information.",
      '',
      `Payment due date: ${paymentDueLabel}`,
      '',
      'If you have a specific timeline for payment processes please let us know so we can plan ahead and update our invoice system for next quarter.',
      '',
      'If you have any questions or need anything at all please let Aspen and I know.',
      '',
      'All the best,',
      signatureName,
      'Overture',
      '',
      'Portal link: (generated when you resend)',
    ].join('\n');

    return { subject, body };
  }, [invoice, invoiceTemplate, school, termId]);

  const contactOptions = useMemo(
    () => schoolContacts.map(c => ({
      id: String(c.id),
      label: `${String(c.displayName || '').trim() || '(no name)'}${(c as any).email ? ` — ${(c as any).email}` : ''}`,
    })),
    [schoolContacts]
  );

  const addAuthorizedContact = useCallback(async () => {
    if (!resolvedPartnerId) {
      setError('Historical school contacts are read-only. Map this location to a partner before changing recipients.');
      return;
    }
    const id = String(authorizedContactPickId || '').trim();
    if (!id) return;
    if (authorizedContactIds.includes(id)) {
      setAuthorizedContactPickId('');
      setAuthorizedContactQuery('');
      return;
    }
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const next = [...authorizedContactIds, id];
      await writeBillingProfile({
        ...(billingProfile as any),
        authorizedContactIds: next,
        updatedAt: Timestamp.now(),
        updatedBy: userData?.id || '',
      } as any);
      setBillingProfile(p => ({ ...(p as any), authorizedContactIds: next } as any));
      setBillingProfileBaseline(p => ({ ...(p as any), authorizedContactIds: next } as any));
      setAuthorizedContactPickId('');
      setAuthorizedContactQuery('');
      setInfo('Authorized contact added.');
    } catch (e: any) {
      setError(e?.message || 'Failed to add authorized contact.');
    } finally {
      setSaving(false);
    }
  }, [authorizedContactIds, authorizedContactPickId, billingProfile, resolvedPartnerId, userData?.id, writeBillingProfile]);

  const removeAuthorizedContact = useCallback(async (contactId: string) => {
    if (!resolvedPartnerId) {
      setError('Historical school contacts are read-only. Map this location to a partner before changing recipients.');
      return;
    }
    const cid = String(contactId || '').trim();
    if (!cid) return;
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const next = authorizedContactIds.filter(x => x !== cid);
      await writeBillingProfile({
        ...(billingProfile as any),
        authorizedContactIds: next,
        billingContactId: (String((billingProfile as any)?.billingContactId || '').trim() === cid) ? '' : (billingProfile as any)?.billingContactId,
        updatedAt: Timestamp.now(),
        updatedBy: userData?.id || '',
      } as any);
      setBillingProfile(p => ({
        ...(p as any),
        authorizedContactIds: next,
        billingContactId: (String((p as any)?.billingContactId || '').trim() === cid) ? undefined : (p as any)?.billingContactId,
      } as any));
      setBillingProfileBaseline(p => ({
        ...(p as any),
        authorizedContactIds: next,
        billingContactId: (String((p as any)?.billingContactId || '').trim() === cid) ? undefined : (p as any)?.billingContactId,
      } as any));
      setInfo('Authorized contact removed.');
    } catch (e: any) {
      setError(e?.message || 'Failed to remove authorized contact.');
    } finally {
      setSaving(false);
    }
  }, [authorizedContactIds, billingProfile, resolvedPartnerId, userData?.id, writeBillingProfile]);

  const setPrimaryBillingContact = useCallback(async (contactId: string | null) => {
    if (!resolvedPartnerId) {
      setError('Historical school contacts are read-only. Map this location to a partner before changing recipients.');
      return;
    }
    const cid = String(contactId || '').trim();
    // Must be one of the authorized contacts (or clear).
    if (cid && !authorizedContactIds.includes(cid)) {
      setError('Billing contact must be an authorized contact.');
      return;
    }
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      await writeBillingProfile({
        ...(billingProfile as any),
        billingContactId: cid || '',
        updatedAt: Timestamp.now(),
        updatedBy: userData?.id || '',
      } as any);
      setBillingProfile(p => ({ ...(p as any), billingContactId: cid || undefined } as any));
      setBillingProfileBaseline(p => ({ ...(p as any), billingContactId: cid || undefined } as any));
      setInfo(cid ? 'Billing contact updated.' : 'Billing contact cleared.');
    } catch (e: any) {
      setError(e?.message || 'Failed to update billing contact.');
    } finally {
      setSaving(false);
    }
  }, [authorizedContactIds, billingProfile, resolvedPartnerId, userData?.id, writeBillingProfile]);

  const invoiceKey = useCallback((inv: Invoice | null) => {
    if (!inv) return '';
    const items = Array.isArray(inv.lineItems) ? inv.lineItems : [];
    return JSON.stringify({
      termId: String((inv as any).termId || ''),
      servicePeriodStart: String((inv as any).servicePeriodStart || ''),
      servicePeriodEnd: String((inv as any).servicePeriodEnd || ''),
      invoiceDate: String(inv.invoiceDate || ''),
      invoiceDateSource: String((inv as any).invoiceDateSource || ''),
      invoiceSendDate: String(inv.invoiceSendDate || ''),
      paymentDueDate: String(inv.paymentDueDate || ''),
      paymentTermsSource: String((inv as any).paymentTermsSource || ''),
      paymentDueDateSource: String((inv as any).paymentDueDateSource || ''),
      internalInvoiceNumberSnapshot: String(inv.internalInvoiceNumberSnapshot || ''),
      lineItems: items.map(li => ({
        description: String(li.description || ''),
        rateCents: Number(li.rateCents || 0),
        quantity: Number(li.quantity || 0),
        paymentMode: String((li as any).paymentMode || ''),
        squadId: String((li as any).squadId || ''),
        squadTitle: String((li as any).squadTitle || ''),
        serviceStartDate: String((li as any).serviceStartDate || ''),
        serviceEndDate: String((li as any).serviceEndDate || ''),
        classesCount: String((li as any).classesCount ?? ''),
        manual: Boolean((li as any).manual),
        manualSourceSquadId: String((li as any).manualSourceSquadId || ''),
      })),
    });
  }, []);

  const invoiceDirty = useMemo(() => invoiceKey(invoice) !== invoiceKey(invoiceBaseline), [invoice, invoiceBaseline, invoiceKey]);

  useEffect(() => {
    if (!invoice?.id || (invoice as any).sentAt) return;
    const openedRevision = Number((invoice as any).generatedRevision || 0);
    return onSnapshot(doc(db, 'invoices', invoice.id), (snapshot) => {
      if (!snapshot.exists()) return;
      const remoteRevision = Number((snapshot.data() as any).generatedRevision || 0);
      if (remoteRevision > openedRevision) setInfo('This draft was updated from newer squad, roster, schedule, or pricing data. Reload before continuing.');
    });
  }, [invoice?.id, (invoice as any)?.generatedRevision, (invoice as any)?.sentAt]);

  const isStripeSquad = useCallback((sq: Squad) => {
    const mode = String((sq as any)?.paymentMode || '').trim().toLowerCase();
    return mode === 'stripe';
  }, []);
  const paymentModeLabel = useCallback((raw: unknown) => {
    const mode = String(raw || '').trim().toLowerCase();
    if (mode === 'school_enrollment' || mode === 'external') return 'Partner enrollment';
    if (mode === 'school_flat_fee') return 'Partner flat fee';
    if (mode === 'stripe') return 'Stripe';
    return 'Unspecified';
  }, []);

  const squadStartYmd = useCallback((sq: Squad): string => safeIsoDate(String((sq as any)?.schedule?.mainDate || (sq as any)?.schedule?.startDate || '').slice(0, 10)), []);
  const squadEndYmd = useCallback((sq: Squad): string => safeIsoDate(String((sq as any)?.schedule?.mainDate || (sq as any)?.schedule?.endDate || '').slice(0, 10)), []);

  const squadsById = useMemo(() => {
    const m = new Map<string, Squad>();
    (Array.isArray(squads) ? squads : []).forEach(sq => {
      const id = String((sq as any)?.id || '').trim();
      if (id) m.set(id, sq);
    });
    return m;
  }, [squads]);

  const isLinkedLineItem = useCallback((li: InvoiceLineItem) => {
    const sid = String((li as any)?.squadId || '').trim();
    return Boolean(sid) && ((li as any).manual !== true || (li as any).customSessionPricing === true);
  }, []);

  const toManualLineItem = useCallback((li: InvoiceLineItem) => {
    const sid = String((li as any)?.squadId || '').trim();
    const next: any = { ...(li as any), manual: true };
    if (sid) next.manualSourceSquadId = sid;
    // Keep squad/dates/classes/type context for display + PDF, but mark as manual so it
    // doesn't get treated as auto-generated.
    return next as InvoiceLineItem;
  }, []);

  const applySquadSelection = useCallback((nextIdsRaw: string[]) => {
    const nextIds = Array.from(new Set((nextIdsRaw || []).map(x => String(x || '').trim()).filter(Boolean)));
    setSelectedSquadIds(nextIds);
    setCoverageDirty(true);

    setInvoice(inv => {
      if (!inv) return inv;

      const allSelected: Squad[] = nextIds
        .map(id => squadsById.get(id))
        .filter(Boolean) as Squad[];
      const selectedBillable = allSelected.filter(sq => !isStripeSquad(sq));

      const existing = Array.isArray(inv.lineItems) ? inv.lineItems : [];
      const manualLines = existing
        .filter(li => !isLinkedLineItem(li) || (li as any).customSessionPricing)
        // If a manual line is an override for a squad-derived line, keep it only while that squad remains selected.
        .filter(li => {
          const sid = String((li as any)?.manualSourceSquadId || '').trim();
          return !sid || nextIds.includes(sid);
        });
      const manualReplacementSquadIds = new Set(
        manualLines.map(li => String((li as any)?.manualSourceSquadId || '').trim()).filter(Boolean)
      );
      const squadsForGeneration = selectedBillable.filter(sq => !manualReplacementSquadIds.has(String((sq as any)?.id || '').trim()));
      const generatedLines = buildInvoiceLineItemsFromSquads({ squads: squadsForGeneration });

      const nextLineItems: InvoiceLineItem[] = [...generatedLines, ...manualLines];

      const range = computeServiceRangeFromSquads(selectedBillable);
      const hasManualCoverageWindow = ['adhoc', 'replacement'].includes(String((inv as any).invoiceKind || ''));
      const serviceStart = hasManualCoverageWindow ? String(inv.servicePeriodStart || '') : safeIsoDate(range?.startDate || '');
      const serviceEnd = hasManualCoverageWindow ? String(inv.servicePeriodEnd || '') : safeIsoDate(range?.endDate || '');

      const invoiceDateNext = serviceStart ? serviceStart : String(inv.invoiceDate || '');
      const netPaymentDays = Number.isFinite(Number(billingProfile.netPaymentDays)) ? Number(billingProfile.netPaymentDays) : 0;
      const paymentDueDateNext = serviceStart ? addDaysYmd(invoiceDateNext, netPaymentDays) : String(inv.paymentDueDate || '');

      const termLabel = String(inv.schoolTermSnapshot || programTermLabelFromId(String((inv as any)?.termId || termId)));
      const squadTitles = selectedBillable.map(sq => String((sq as any)?.title || '').trim()).filter(Boolean);
      const serviceDescriptionSnapshot =
        squadTitles.length
          ? `Overture programming — ${termLabel} — ${squadTitles.join(', ')}`
          : `Overture programming — ${termLabel}`;

      return {
        ...inv,
        coveredSquadIds: nextIds,
        lineItems: nextLineItems,
        servicePeriodStart: serviceStart || undefined,
        servicePeriodEnd: serviceEnd || undefined,
        serviceDatesSnapshot: serviceStart || serviceEnd ? { startDate: serviceStart || undefined, endDate: serviceEnd || undefined } : {},
        serviceDescriptionSnapshot,
        invoiceDate: invoiceDateNext,
        paymentDueDate: paymentDueDateNext,
      } as Invoice;
    });
  }, [billingProfile.netPaymentDays, isLinkedLineItem, isStripeSquad, squadsById, termId]);

  // Keep local selection in sync with the invoice source-of-truth.
  useEffect(() => {
    if (!invoice) return;
    const covered = Array.isArray((invoice as any)?.coveredSquadIds) ? (invoice as any).coveredSquadIds : [];
    const fromCovered = covered.map((x: any) => String(x || '').trim()).filter(Boolean);
    const fromLineItems =
      Array.isArray(invoice.lineItems)
        ? invoice.lineItems.map(li => String((li as any)?.squadId || '').trim()).filter(Boolean)
        : [];
    const ids = Array.from(new Set([...fromCovered, ...fromLineItems]));
    setSelectedSquadIds(ids);
  }, [invoice]);


  const loadAll = useCallback(async () => {
    // Can load either by explicit invoiceId (preferred) or by school+term (legacy entrypoint).
    if (!resolvedInvoiceId && !resolvedSchoolId) return;
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      let invObj: Invoice | null = null;
      let effectiveSchoolId = String(resolvedSchoolId || '').trim();
      let effectiveTermId = String(termId || '').trim();

      // 1) If we have an explicit invoiceId, load it and derive school/term from it (if present).
      if (resolvedInvoiceId) {
        const invId = String(resolvedInvoiceId || '').trim();
        const invSnap = await getDoc(doc(db, 'invoices', invId));
        if (!invSnap.exists()) throw new Error('Invoice not found.');
        invObj = { id: invId, ...(invSnap.data() as any) } as Invoice;
        if (invObj.legacy === true) throw new Error('Legacy invoices are read-only. Open this invoice from the Legacy invoices table.');
        if (!String(invObj.partnerId || '').trim()) throw new Error('Current invoices require a partnerId.');
        effectiveSchoolId = String((invObj as any)?.schoolId || '').trim();
        if (!effectiveSchoolId && (invObj as any)?.locationId) {
          const locationSnap = await getDoc(doc(db, 'locations', String((invObj as any).locationId)));
          effectiveSchoolId = locationSnap.exists()
            ? String((locationSnap.data() as any)?.legacySchoolId || '').trim()
            : '';
        }
        if (!effectiveSchoolId && !(invObj as any)?.partnerId && !(invObj as any)?.locationId) {
          throw new Error('This canonical invoice location has no legacy school bridge for the current editor.');
        }
        const invTermId = String((invObj as any)?.termId || '').trim();
        if (invTermId && invTermId !== effectiveTermId && !termIdFromQuery) {
          setTermId(invTermId);
          effectiveTermId = invTermId;
        }
        if (effectiveSchoolId !== resolvedSchoolId) {
          setResolvedSchoolId(effectiveSchoolId);
        }
      } else {
        // 2) Otherwise, best-effort: pick the most recently created invoice for this school+term (if any).
        // This keeps the legacy /admin/billing/:schoolId entrypoint usable.
        if (effectiveSchoolId && effectiveTermId) {
          const invSnap = await getDocs(query(
            collection(db, 'invoices'),
            where('schoolId', '==', effectiveSchoolId),
            where('termId', '==', effectiveTermId),
          ));
          let list = invSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) } as Invoice));
          if (list.length === 0) {
            const legacySchool = await getDoc(doc(db, 'schools', effectiveSchoolId));
            const locationId = legacySchool.exists()
              ? String((legacySchool.data() as any)?.locationId || '').trim()
              : '';
            if (locationId) {
              const canonicalSnap = await getDocs(query(
                collection(db, 'invoices'),
                where('locationId', '==', locationId),
                where('termId', '==', effectiveTermId),
              ));
              list = canonicalSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) } as Invoice));
            }
          }
          list = list.filter((candidate: any) => !candidate?.canceledAt && candidate?.status !== 'canceled');
          list.sort((a: any, b: any) => {
            const am = a?.createdAt?.toMillis?.() ?? 0;
            const bm = b?.createdAt?.toMillis?.() ?? 0;
            if (am !== bm) return bm - am;
            const au = a?.updatedAt?.toMillis?.() ?? 0;
            const bu = b?.updatedAt?.toMillis?.() ?? 0;
            return bu - au;
          });
          invObj = list[0] || null;
        }
      }

      let canonicalPartnerId = String((invObj as any)?.partnerId || '').trim();
      let canonicalLocationId = String((invObj as any)?.locationId || '').trim();
      let s: School;
      if (canonicalPartnerId && canonicalLocationId) {
        const locationSnap = await getDoc(doc(db, 'locations', canonicalLocationId));
        if (!locationSnap.exists()) throw new Error('Canonical invoice location not found.');
        const location = locationSnap.data() as any;
        if (String(location?.partnerId || '').trim() !== canonicalPartnerId) {
          throw new Error('Canonical invoice partner/location mismatch.');
        }
        s = {
          id: canonicalLocationId,
          ...location,
          name: String(location?.name || canonicalLocationId),
          partnerId: canonicalPartnerId,
          locationId: canonicalLocationId,
        } as School;
      } else if (canonicalPartnerId) {
        const partnerSnap = await getDoc(doc(db, 'partners', canonicalPartnerId));
        if (!partnerSnap.exists()) throw new Error('Canonical invoice partner not found.');
        const partner = partnerSnap.data() as any;
        s = {
          id: canonicalPartnerId,
          ...partner,
          name: String(partner?.name || canonicalPartnerId),
          partnerId: canonicalPartnerId,
        } as School;
      } else {
        console.warn('[BillingCutover] Loading targeted historical school record read-only', {
          schoolId: effectiveSchoolId,
          invoiceId: invObj?.id || null,
          action: 'read-only',
        });
        const schoolSnap = await getDoc(doc(db, 'schools', effectiveSchoolId));
        if (!schoolSnap.exists()) throw new Error('Historical school record not found.');
        s = { id: effectiveSchoolId, ...(schoolSnap.data() as any) } as School;
        canonicalPartnerId = String((s as any)?.partnerId || '').trim();
        canonicalLocationId = String((s as any)?.locationId || '').trim();
      }
      setSchool(s);
      setResolvedPartnerId(canonicalPartnerId);
      setResolvedLocationId(canonicalLocationId);
      if (!canonicalPartnerId) {
        console.warn('[BillingCutover] Loading historical school billing and contacts read-only', {
          schoolId: effectiveSchoolId,
          invoiceId: invObj?.id || null,
          action: 'read-only',
        });
      }

      // Canonical partner root is the SSOT. School profile is historical read compatibility.
      const profSnap = canonicalPartnerId
        ? await getDoc(doc(db, 'partners', canonicalPartnerId))
        : await getDoc(doc(db, 'schools', effectiveSchoolId, 'billing', 'profile'));
      const prof = (profSnap.exists()
        ? (canonicalPartnerId ? ((profSnap.data() as any)?.billingDefaults || {}) : (profSnap.data() as any))
        : {}) as BillingProfile;
      setBillingProfile(prof);
      setBillingProfileBaseline(prof);

      // School contacts (used for authorized billing contacts)
      try {
        const cSnap = canonicalPartnerId
          ? await getDocs(collection(db, 'partners', canonicalPartnerId, 'contacts'))
          : await getDocs(collection(db, 'schools', effectiveSchoolId, 'contacts'));
        const contacts = cSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) } as SchoolContactWithId));
        contacts.sort((a, b) => String(a.displayName || '').localeCompare(String(b.displayName || '')));
        setSchoolContacts(contacts);
      } catch {
        setSchoolContacts([]);
      }

      // Squads for selection + line item derivation use canonical location
      // identity, with a targeted historical fallback only for old invoices.
      const squadsSnap = await getDocs(query(
        collection(db, 'squads'),
        where(canonicalLocationId ? 'locationId' : canonicalPartnerId ? 'partnerId' : 'schoolId', '==', canonicalLocationId || canonicalPartnerId || effectiveSchoolId)
      ));
      const allSquads = squadsSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) } as Squad));
      setSquads(allSquads);

      // Preserve the exact Firestore shape as the baseline, so we can mark the editor "dirty"
      // if we hydrate missing fields locally (e.g., termId on an empty draft).
      const invBaseline: Invoice | null = invObj ? ({ ...(invObj as any) } as Invoice) : null;

      // If invoice exists but is missing term metadata (common for newly created empty drafts),
      // hydrate local defaults so the editor is usable immediately.
      if (invObj) {
        const invTerm = String((invObj as any)?.termId || '').trim();
        if (!invTerm && effectiveTermId) {
          invObj = {
            ...(invObj as any),
            termId: effectiveTermId,
            schoolTermSnapshot: (invObj as any).schoolTermSnapshot || programTermLabelFromId(effectiveTermId),
          } as Invoice;
        }
      }

      setInvoice(invObj);
      setInvoiceBaseline(invBaseline);
      // Invoice template (for defaults + navigation)
      const tSnap = await getDoc(doc(db, 'invoiceTemplate', 'current'));
      setInvoiceTemplate(
        tSnap.exists()
          ? ({ id: 'current', ...(DEFAULT_TEMPLATE as any), ...(tSnap.data() as any) } as InvoiceTemplate)
          : ({ id: 'current', ...(DEFAULT_TEMPLATE as any) } as InvoiceTemplate)
      );

      // Portal tokens (admin visibility; portal itself is server-mediated)
      if (invObj) {
        const invId = String(invObj.id);
        const tokensSnap = await getDocs(query(collection(db, 'invoiceAccessTokens'), where('invoiceId', '==', invId)));
        const toks = tokensSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as InvoiceAccessToken[];
        toks.sort((a, b) => String(a.recipientEmail || '').localeCompare(String(b.recipientEmail || '')));
        setPortalTokens(toks);

        // Payment confirmations submitted via portal (read-only audit)
        const psSnap = await getDocs(collection(db, 'invoices', invId, 'paymentSubmissions'));
        const subs = psSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as InvoicePaymentSubmission[];
        subs.sort((a, b) => String((b as any)?.submittedAt?.toDate?.()?.toISOString?.() || '').localeCompare(String((a as any)?.submittedAt?.toDate?.()?.toISOString?.() || '')));
        setPaymentSubmissions(subs);
      } else {
        setPortalTokens([]);
        setPaymentSubmissions([]);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load billing data.');
    } finally {
      setLoading(false);
    }
  }, [resolvedInvoiceId, resolvedSchoolId, termId, termIdFromQuery]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const ensureInvoice = useCallback(async () => {
    if (!school || !resolvedSchoolId || !termId) return;
    if (!resolvedPartnerId) {
      setError('This location has not been linked to a canonical partner.');
      return;
    }
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const now = new Date();
      const today = now.toISOString().slice(0, 10);
      const termLabel = programTermLabelFromId(termId);

      // Start with an empty selection; selection drives service period + line items.
      const serviceStart = '';
      const serviceEnd = '';

      // Net terms: compute default due date from today until squads are selected.
      const netInvoiceDays =
        Number.isFinite(Number(billingProfile.netInvoiceDays)) ? Number(billingProfile.netInvoiceDays) : 0;
      const netPaymentDays =
        Number.isFinite(Number(billingProfile.netPaymentDays)) ? Number(billingProfile.netPaymentDays) : 0;
      const invoiceSendDate = addDaysYmd(today, netInvoiceDays);
      const invoiceDate = today;
      const paymentDueDate = addDaysYmd(invoiceDate, netPaymentDays);

      const legalName = String(billingProfile.legalName || '').trim();
      const internal = generateInternalInvoiceNumber({ invoiceDate: today, schoolName: school.name, termId });

      const defaultLineItems: InvoiceLineItem[] = [];
      const subtotal = 0;
      const coveredSquadIds: string[] = [];

      const docData: Omit<Invoice, 'id'> = {
        legacy: false,
        ...(resolvedPartnerId ? { partnerId: resolvedPartnerId } : {}),
        ...(resolvedLocationId ? { locationId: resolvedLocationId } : {}),
        termId,
        servicePeriodStart: serviceStart || undefined,
        servicePeriodEnd: serviceEnd || undefined,
        coveredSquadIds,
        invoiceDate,
        invoiceDateSource: 'send_date',
        invoiceSendDate,
        paymentDueDate,

        schoolNameSnapshot: school.name,
        schoolTermSnapshot: termLabel,
        overtureNameSnapshot: String((invoiceTemplate as any)?.overtureName || DEFAULT_TEMPLATE.overtureName || 'Overture'),
        overtureAddressSnapshot: String((invoiceTemplate as any)?.overtureAddress || DEFAULT_TEMPLATE.overtureAddress || ''),
        paymentDescriptionSnapshot: 'flat tuition per student for the session',
        serviceDescriptionSnapshot: `Overture programming — ${termLabel}`,
        serviceDatesSnapshot: { startDate: serviceStart, endDate: serviceEnd },
        internalInvoiceNumberSnapshot: internal,
        netInvoiceSnapshot: netInvoiceDays,
        netPaymentSnapshot: netPaymentDays,
        paymentTermsSource: 'partner_default',
        paymentDueDateSource: 'terms',
        payerLegalNameSnapshot: legalName || school.name,
        payerBillingAddressSnapshot: billingProfile.billingAddress || {},
        paymentInstructionsSnapshot: String((invoiceTemplate as any)?.paymentInstructions || DEFAULT_TEMPLATE.paymentInstructions || ''),

        currency: 'USD',
        lineItems: defaultLineItems,
        subtotalCents: subtotal,
        totalCents: subtotal,
        status: 'draft',
        revision: 0,

        createdAt: Timestamp.now(),
        createdBy: userData?.id || '',
        updatedAt: Timestamp.now(),
        updatedBy: userData?.id || '',
      };

      const created = await addDoc(collection(db, 'invoices'), docData as any);
      setResolvedInvoiceId(created.id);
      setInfo('Invoice created.');
      navigate(`/app/admin/invoices/${created.id}`);
    } catch (e: any) {
      setError(e?.message || 'Failed to initialize invoice.');
    } finally {
      setSaving(false);
    }
  }, [billingProfile, invoiceTemplate, navigate, resolvedLocationId, resolvedPartnerId, resolvedSchoolId, school, termId, userData?.id]);

  const saveBillingProfile = useCallback(async (): Promise<boolean> => {
    if (!resolvedPartnerId) return false;
    if (!termsDirty) return true;
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      await writeBillingProfile({
        ...billingProfile,
        updatedAt: Timestamp.now(),
        updatedBy: userData?.id || '',
      });
      setBillingProfileBaseline(billingProfile);
      setInfo('Saved ✓');
      return true;
    } catch (e: any) {
      setError(e?.message || 'Failed to save billing profile.');
      return false;
    } finally {
      setSaving(false);
    }
  }, [billingProfile, resolvedPartnerId, termsDirty, userData?.id, writeBillingProfile]);

  const saveInvoice = useCallback(async (opts?: { source?: 'auto' | 'manual' }): Promise<boolean> => {
    if (!invoice) return false;
    if (!invoiceDirty) return true;
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const invRef = doc(db, 'invoices', invoice.id);
      const latestSnapshot = await getDoc(invRef);
      const latestRevision = Number((latestSnapshot.data() as any)?.generatedRevision || 0);
      const openedRevision = Number((invoiceBaseline as any)?.generatedRevision || 0);
      if (latestRevision > openedRevision) throw new Error('This draft was updated while you were editing. Reload it before saving so newer session claims are not overwritten.');
      const lineItems = (invoice.lineItems || []).map(li => ({
        ...li,
        rateCents: cents(li.rateCents),
        quantity: (isLinkedLineItem(li) && li.paymentMode === 'school_flat_fee' && !Array.isArray((li as any).classDates))
          ? 1
          : Math.max(0, Math.round(Number(li.quantity || 0))),
        amountCents: computeLineItemAmount(li),
      }));
      const subtotal = lineItems.reduce((sum, li) => sum + (li.amountCents || 0), 0);
      const explicitCovered = Array.isArray((invoice as any)?.coveredSquadIds)
        ? (invoice as any).coveredSquadIds.map((x: any) => String(x || '').trim()).filter(Boolean)
        : [];
      const coveredFromLines = lineItems.map(li => String((li as any)?.squadId || '').trim()).filter(Boolean);
      const coveredFromManualSources = lineItems.map(li => String((li as any)?.manualSourceSquadId || '').trim()).filter(Boolean);
      const coveredSquadIds = Array.from(new Set([...explicitCovered, ...coveredFromLines, ...coveredFromManualSources]));
      const servicePeriodStart =
        String((invoice as any)?.servicePeriodStart || '').trim()
          || String((invoice as any)?.serviceDatesSnapshot?.startDate || '').trim()
          || undefined;
      const servicePeriodEnd =
        String((invoice as any)?.servicePeriodEnd || '').trim()
          || String((invoice as any)?.serviceDatesSnapshot?.endDate || '').trim()
          || undefined;
      const payload = {
        ...invoice,
        lineItems,
        subtotalCents: subtotal,
        totalCents: subtotal,
        coveredSquadIds,
        servicePeriodStart,
        servicePeriodEnd,
        serviceDatesSnapshot: {
          startDate: servicePeriodStart,
          endDate: servicePeriodEnd,
        },
        updatedAt: Timestamp.now(),
        updatedBy: userData?.id || '',
      };
      // Never write local id field as nested data; Firestore doc id is separate.
      const { id, ...rest } = payload as any;
      await updateDoc(invRef, stripUndefinedDeep(rest));
      const nextLocal: Invoice = { ...(invoice as any), ...(rest as any), id: invoice.id } as any;
      setInvoice(nextLocal);
      setInvoiceBaseline(nextLocal);
      setAutoSaveFailedKey(null);
      setInfo('Saved ✓');
      await loadAll();
      return true;
    } catch (e: any) {
      setError(e?.message || 'Failed to save invoice.');
      if (opts?.source === 'auto') {
        setAutoSaveFailedKey(invoiceKey(invoice));
      }
      return false;
    } finally {
      setSaving(false);
    }
  }, [invoice, invoiceBaseline, invoiceDirty, invoiceKey, isLinkedLineItem, loadAll, userData?.id]);

  useEffect(() => {
    if (!pdfGenerating) return;
    const id = window.setInterval(() => setPdfGenTick(t => t + 1), 250);
    return () => window.clearInterval(id);
  }, [pdfGenerating]);

  const pdfGenElapsedSeconds = useMemo(() => {
    if (!pdfGenerating || !pdfGenStartedAtMs) return 0;
    // pdfGenTick is intentionally referenced to force repaint while generating.
    void pdfGenTick;
    return Math.max(0, Math.floor((Date.now() - pdfGenStartedAtMs) / 1000));
  }, [pdfGenStartedAtMs, pdfGenerating, pdfGenTick]);

  // Auto-save invoice changes (debounced). Prevents endless retry on a hard failure until the next edit.
  useEffect(() => {
    if (!invoice) return;
    if (!invoiceDirty) return;
    if (saving || loading) return;

    const key = invoiceKey(invoice);
    if (autoSaveFailedKey && autoSaveFailedKey === key) return;

    if (autoSaveTimerRef.current) {
      window.clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    autoSaveTimerRef.current = window.setTimeout(() => {
      saveInvoice({ source: 'auto' });
    }, 900);

    return () => {
      if (autoSaveTimerRef.current) {
        window.clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, [autoSaveFailedKey, invoice, invoiceDirty, invoiceKey, loading, saveInvoice, saving]);

  const autoSaveStatus = useMemo(() => {
    if (!invoice) return null;
    const key = invoiceKey(invoice);
    if (saving) return { tone: 'saving' as const, text: 'Saving…' };
    if (invoiceDirty) {
      if (autoSaveFailedKey && autoSaveFailedKey === key) return { tone: 'error' as const, text: 'Save failed' };
      return { tone: 'saving' as const, text: 'Saving…' };
    }
    return { tone: 'saved' as const, text: 'Saved ✓' };
  }, [autoSaveFailedKey, invoice, invoiceDirty, invoiceKey, saving]);

  const filteredSquads = useMemo(() => {
    const list = Array.isArray(squads) ? squads : [];
    const q = String(squadSearch || '').trim().toLowerCase();
    const out = list.filter(sq => {
      // Stripe squads are not invoiceable; omit from selector UI.
      if (isStripeSquad(sq)) return false;
      const squadTermId = String(resolveSquadTermId(sq) || '');
      if (invoice?.termId && squadTermId && squadTermId !== invoice.termId) return false;
      if (!q) return true;
      const title = String((sq as any)?.title || '').toLowerCase();
      const id = String((sq as any)?.id || '').toLowerCase();
      return title.includes(q) || id.includes(q);
    });
    out.sort((a, b) => {
      const as = squadStartYmd(a);
      const bs = squadStartYmd(b);
      if (as && bs && as !== bs) return as.localeCompare(bs);
      return String((a as any)?.title || '').localeCompare(String((b as any)?.title || ''));
    });
    return out;
  }, [invoice?.termId, isStripeSquad, squadSearch, squadStartYmd, squads]);

  const enrollmentWarnings = useMemo(() => {
    return (selectedSquadIds || []).flatMap((id) => {
      const squad = squadsById.get(id);
      if (!squad) return [];
      const mode = String((squad as any)?.paymentMode || '').trim().toLowerCase();
      const isEnrollmentInvoice = mode === 'school_enrollment' || mode === 'external';
      const isDemo = (squad as any)?.scheduleKind === 'single_day' && (squad as any)?.singleDayType === 'demo';
      if (!isEnrollmentInvoice || isDemo) return [];
      const enrolled = Array.isArray((squad as any)?.studentIds) ? (squad as any).studentIds.length : 0;
      const minimum = Number((squad as any)?.minEnroll);
      const capacity = Number((squad as any)?.capacity);
      const name = String((squad as any)?.title || id || 'Squad');
      if (enrolled === 0) return [{ code: 'ZERO_HEADCOUNT', squadId: id, text: `${name} has zero enrolled students. Confirm the headcount before sending.` }];
      const warnings: Array<{ code: string; squadId: string; text: string }> = [];
      if (Number.isFinite(minimum) && enrolled < minimum) warnings.push({ code: 'BELOW_MINIMUM', squadId: id, text: `${name} has ${enrolled} enrolled, below its minimum of ${minimum}.` });
      if (Number.isFinite(capacity) && enrolled > capacity) warnings.push({ code: 'OVER_CAPACITY', squadId: id, text: `${name} has ${enrolled} enrolled, above its capacity of ${capacity}.` });
      return warnings;
    });
  }, [selectedSquadIds, squadsById]);

  const enrollmentWarningSignature = useMemo(
    () => enrollmentWarnings.map((warning) => `${warning.code}:${warning.squadId}`).join('|'),
    [enrollmentWarnings]
  );
  useEffect(() => {
    setEnrollmentWarningsAcknowledged(false);
  }, [enrollmentWarningSignature]);

  const generatePdf = useCallback(async (opts?: { openPreview?: boolean }): Promise<Invoice['pdf'] | null> => {
    if (!invoice) return null;
    setPdfGenerating(true);
    setPdfGenStartedAtMs(Date.now());
    setSaving(true);
    setError(null);
    setSendPreviewError(null);
    setInfo(null);
    try {
      // IMPORTANT:
      // The PDF generation runs in a Cloud Function. In production, that function may still
      // prefer invoice snapshot fields over the latest template. To guarantee the newest
      // template content is used right now, sync template fields into the invoice snapshots
      // immediately before generating the PDF.
      try {
        const tSnap = await getDoc(doc(db, 'invoiceTemplate', 'current'));
        const t: any = tSnap.exists() ? (tSnap.data() as any) : {};
        const updates: any = {};
        const pi = String(t?.paymentInstructions || '').trim();
        const on = String(t?.overtureName || '').trim();
        const oa = String(t?.overtureAddress || '').trim();
        if (pi) updates.paymentInstructionsSnapshot = pi;
        if (on) updates.overtureNameSnapshot = on;
        if (oa) updates.overtureAddressSnapshot = oa;
        if (Object.keys(updates).length > 0) {
          updates.updatedAt = Timestamp.now();
          updates.updatedBy = userData?.id || '';
          await updateDoc(doc(db, 'invoices', invoice.id), updates);
        }
      } catch (_) {
        // Best-effort; PDF generation can proceed even if sync fails.
      }
      if (coverageDirty) {
        const configure = httpsCallable(getFunctions(), 'configureAdHocInvoice');
        await configure({
          invoiceId: invoice.id,
          squadIds: selectedSquadIds,
          startDate: invoice.servicePeriodStart || null,
          endDate: invoice.servicePeriodEnd || null,
          expectedRevision: Number((invoice as any).revision || 0),
        });
        setCoverageDirty(false);
      }
      const fn = httpsCallable(getFunctions(), 'generateInvoicePdf');
      const result: any = await fn({ invoiceId: invoice.id });
      const generatedPdf = result?.data?.pdf || null;
      if (generatedPdf) setInvoice(current => current ? ({ ...current, pdf: generatedPdf }) : current);
      await loadAll();
      // The fresh PDF is now authoritative. Clearing the URL captured when the
      // modal opened lets the first generated preview render immediately.
      if (generatedPdf?.url) setSendPreviewPdfUrlAtOpen(null);
      if (opts?.openPreview ?? true) setPdfPreviewOpen(true);
      return generatedPdf;
    } catch (e: any) {
      const message = e?.message || 'Failed to generate invoice PDF.';
      setError(message);
      setSendPreviewError(message);
      return null;
    } finally {
      setSaving(false);
      setPdfGenerating(false);
    }
  }, [coverageDirty, invoice, loadAll, selectedSquadIds, userData?.id]);

  const sendInvoice = useCallback(async (): Promise<boolean> => {
    if (!invoice) return false;
    if (enrollmentWarnings.length > 0 && !enrollmentWarningsAcknowledged) {
      const message = 'Acknowledge the enrollment warnings before sending this invoice.';
      setError(message);
      setSendActionError(message);
      return false;
    }
    if (invoiceDirty) {
      const message = 'Please wait for invoice changes to finish saving before sending.';
      setError(message);
      setSendActionError(message);
      return false;
    }
    setSaving(true);
    setError(null);
    setSendActionError(null);
    setInfo(null);
    try {
      const fn = httpsCallable(getFunctions(), 'sendInvoiceEmail');
      const res: any = await fn({ invoiceId: invoice.id });
      setInfo(`Invoice sent to ${res?.data?.sentToCount || 0} recipients.`);
      await loadAll();
      return true;
    } catch (e: any) {
      console.error('[Billing] sendInvoiceEmail rejected', {
        code: e?.code,
        message: e?.message,
        details: e?.details,
        invoiceId: invoice.id,
      });
      const message = e?.message || 'The invoice could not be sent. Please try again.';
      setError(message);
      setSendActionError(message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [enrollmentWarnings.length, enrollmentWarningsAcknowledged, invoice, invoiceDirty, loadAll]);

  const resendInvoice = useCallback(async (): Promise<boolean> => {
    if (!invoice) return false;
    const sentAlready = Boolean((invoice as any).sentAt);
    if (!sentAlready) {
      const message = 'Invoice has not been sent yet. Use Send invoice first.';
      setError(message);
      setSendActionError(message);
      return false;
    }
    // Confirm happens in the preview modal now.
    setSaving(true);
    setError(null);
    setSendActionError(null);
    setInfo(null);
    try {
      const fn = httpsCallable(getFunctions(), 'resendInvoiceEmail');
      const res: any = await fn({ invoiceId: invoice.id });
      setInfo(`Invoice resent to ${res?.data?.sentToCount || 0} recipients.`);
      await loadAll();
      return true;
    } catch (e: any) {
      const message = e?.message || 'The invoice could not be resent. Please try again.';
      setError(message);
      setSendActionError(message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [invoice, loadAll]);

  const openSendPreview = useCallback((mode: 'send' | 'resend') => {
    if (!invoice) return;
    // For "send", we allow opening even if PDF isn't ready yet; the click handler can generate it first.
    // For "resend", we require an existing PDF.
    if (mode === 'resend' && !invoice.pdf?.url) {
      setError('No invoice PDF found to resend. Generate a PDF first.');
      return;
    }
    if (mode === 'resend' && !(invoice as any)?.sentAt) {
      setError('Invoice has not been sent yet. Use Send invoice first.');
      return;
    }
    setSendPreviewPdfUrlAtOpen(invoice?.pdf?.url || null);
    setSendPreviewError(null);
    setSendActionError(null);
    setSendPreviewOpen(mode);
  }, [invoice]);

  const handleSendInvoiceClick = useCallback(async () => {
    if (!invoice) return;
    try {
      const actualSendDate = billingTodayYmd();
      const plannedSendDate = String(invoice.plannedSendDate || invoice.invoiceSendDate || invoice.invoiceDate || actualSendDate).slice(0, 10);
      const usesPartnerTerms = (invoice as any).paymentTermsSource !== 'manual_override' && (invoice as any).paymentDueDateSource !== 'manual_override' && (invoice as any).paymentTermsSource !== 'unknown';
      const effectiveNetDays = usesPartnerTerms ? billingProfile.netPaymentDays : invoice.netPaymentSnapshot;
      const netDays = Number.isFinite(Number(effectiveNetDays))
        ? Number(effectiveNetDays)
        : 14;
      const plannedDate = new Date(`${plannedSendDate}T00:00:00Z`);
      const actualDate = new Date(`${actualSendDate}T00:00:00Z`);
      const sentTimingDays = Number.isNaN(plannedDate.getTime()) ? 0 : Math.round((actualDate.getTime() - plannedDate.getTime()) / (24 * 60 * 60 * 1000));
      // Flush any pending autosave timer so invoiceDirty reflects current state.
      if (autoSaveTimerRef.current) {
        window.clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }

      // Ensure invoice is persisted before generating PDF / sending.
      if (termsDirty && !(await saveBillingProfile())) return;
      if (invoiceDirty && !(await saveInvoice({ source: 'manual' }))) return;
      const enteredInvoiceDate = safeIsoDate(invoice.invoiceDate || '');
      const enteredDueDate = safeIsoDate(invoice.paymentDueDate || '');
      const manualInvoiceDate = (invoice as any).invoiceDateSource === 'manual_override' && Boolean(enteredInvoiceDate);
      const manualDueDate = (invoice as any).paymentDueDateSource === 'manual_override' && Boolean(enteredDueDate);
      const effectiveInvoiceDate = manualInvoiceDate ? enteredInvoiceDate : actualSendDate;
      const effectiveDueDate = manualDueDate ? enteredDueDate : addDaysYmd(effectiveInvoiceDate, netDays);
      const sendDatePatch: Partial<Invoice> = {
        plannedSendDate,
        invoiceDate: effectiveInvoiceDate,
        invoiceDateSource: manualInvoiceDate ? 'manual_override' : 'send_date',
        paymentDueDate: effectiveDueDate,
        netPaymentSnapshot: netDays,
        paymentTermsSource: usesPartnerTerms ? 'partner_default' : (invoice as any).paymentTermsSource,
        paymentDueDateSource: usesPartnerTerms ? 'terms' : (invoice as any).paymentDueDateSource,
        sentTimingDays,
        updatedAt: Timestamp.now(),
        updatedBy: userData?.id || '',
      };
      await updateDoc(doc(db, 'invoices', invoice.id), sendDatePatch);
      setInvoice((current) => current ? ({ ...current, ...sendDatePatch }) : current);

      // Open preview immediately and generate a fresh PDF for it.
      setSendPreviewError(null);
      setSendActionError(null);
      setSendPreviewOpen('send');
      setSendPreviewPdfUrlAtOpen(invoice?.pdf?.url || null);
      await generatePdf({ openPreview: false });
    } catch (e: any) {
      const message = e?.message || 'Failed to prepare invoice for sending.';
      setError(message);
      setSendActionError(message);
    }
  }, [billingProfile.netPaymentDays, generatePdf, invoice, invoiceDirty, saveBillingProfile, saveInvoice, termsDirty, userData?.id]);

  const confirmSendFromPreview = useCallback(async () => {
    if (!sendPreviewOpen) return;
    const mode = sendPreviewOpen;
    const succeeded = mode === 'send' ? await sendInvoice() : await resendInvoice();
    if (succeeded) setSendPreviewOpen(null);
  }, [resendInvoice, sendInvoice, sendPreviewOpen]);

  const revokeLinks = useCallback(async () => {
    if (!invoice) return;
    setConfirmModal({
      title: 'Revoke all portal links?',
      body: (
        <div>
          This will invalidate existing portal links for this invoice.
          <div className="muted" style={{ marginTop: 8 }}>
            You can send a new invoice email afterwards to generate fresh links.
          </div>
        </div>
      ),
      confirmText: 'Revoke links',
      confirmTone: 'danger',
      onConfirm: async () => {
        setConfirmModal(null);
        setSaving(true);
        setError(null);
        setInfo(null);
        try {
          const fn = httpsCallable(getFunctions(), 'revokeInvoiceLinks');
          const res: any = await fn({ invoiceId: invoice.id });
          setInfo(`Revoked ${res?.data?.revokedCount || 0} links.`);
          await loadAll();
        } catch (e: any) {
          setError(e?.message || 'Failed to revoke links.');
        } finally {
          setSaving(false);
        }
      },
    });
  }, [invoice, loadAll]);

  const cancelInvoiceRecord = useCallback(async () => {
    if (!invoice) return;
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const fn = httpsCallable(getFunctions(), 'cancelInvoice');
      const result: any = await fn({ invoiceId: invoice.id, reason: 'Canceled from invoice detail' });
      setInfo(`Invoice canceled. ${Number(result?.data?.voidedClaims || 0)} session claims voided and ${Number(result?.data?.revokedTokens || 0)} portal links revoked.`);
      await loadAll();
    } catch (e: any) {
      setError(e?.message || 'Failed to cancel invoice.');
    } finally {
      setSaving(false);
    }
  }, [invoice, loadAll]);

  const createReplacement = useCallback(async () => {
    if (!invoice) return;
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      // This is intentionally idempotent and also repairs invoices canceled by
      // the former client-only flow before creating their replacement.
      const cancel = httpsCallable(getFunctions(), 'cancelInvoice');
      await cancel({ invoiceId: invoice.id, reason: (invoice as any)?.cancelReason || 'Canceled invoice replacement' });
      const create = httpsCallable(getFunctions(), 'createReplacementInvoice');
      const result: any = await create({ invoiceId: invoice.id });
      const replacementId = String(result?.data?.invoiceId || '').trim();
      if (!replacementId) throw new Error('Replacement invoice was not returned.');
      navigate(`/app/admin/invoices/${replacementId}`);
    } catch (e: any) {
      setError(e?.message || 'Failed to create replacement invoice.');
    } finally {
      setSaving(false);
    }
  }, [invoice, navigate]);

  const deleteDraftInvoice = useCallback(async () => {
    if (!invoice) return;
    if (isSent) {
      setError('This invoice has been sent and cannot be deleted.');
      return;
    }
    setConfirmModal({
      title: 'Delete draft invoice?',
      body: (
        <div>
          This will permanently delete this invoice draft and its line items.
          <div className="muted" style={{ marginTop: 8 }}>
            This is only allowed before sending.
          </div>
        </div>
      ),
      confirmText: 'Delete draft',
      confirmTone: 'danger',
      onConfirm: async () => {
        setConfirmModal(null);
        setSaving(true);
        setError(null);
        setInfo(null);
        try {
          await deleteDoc(doc(db, 'invoices', invoice.id));
          setInfo('Draft invoice deleted.');
          setInvoice(null);
          setInvoiceBaseline(null);
          setResolvedInvoiceId('');
          if (resolvedPartnerId) {
            navigate(`/app/admin/partners/${resolvedPartnerId}?tab=billing`);
          } else {
            navigate('/app/admin', { state: { adminTab: 'invoicing', invoicingSchoolId: resolvedSchoolId } });
          }
        } catch (e: any) {
          setError(e?.message || 'Failed to delete invoice draft.');
        } finally {
          setSaving(false);
        }
      },
    });
  }, [invoice, isSent, navigate, resolvedPartnerId, resolvedSchoolId]);

  const openCreateAuthorizedContact = useCallback(() => {
    if (!resolvedPartnerId) {
      setError('Historical school contacts are read-only. Map this location to a partner before creating contacts.');
      return;
    }
    setCreateAuthorizedContactDraft({ displayName: '', email: '' });
    setCreateAuthorizedContactModalOpen(true);
  }, [resolvedPartnerId]);

  const confirmCreateAuthorizedContact = useCallback(async () => {
    if (!resolvedPartnerId) {
      setError('Historical school contacts are read-only. Map this location to a partner before creating contacts.');
      return;
    }
    const email = String(createAuthorizedContactDraft.email || '').trim().toLowerCase();
    const displayNameRaw = String(createAuthorizedContactDraft.displayName || '').trim();
    const displayName = displayNameRaw || email;
    if (!email) {
      setError('Email is required.');
      return;
    }

    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const contactsCollection = collection(db, 'partners', resolvedPartnerId, 'contacts');
      const created = await addDoc(contactsCollection, {
        displayName,
        roles: ['finance'],
        locationIds: resolvedLocationId ? [resolvedLocationId] : [],
        email,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      } as any);

      const nextIds = Array.from(new Set([...authorizedContactIds, created.id]));
      await writeBillingProfile({
        ...(billingProfile as any),
        authorizedContactIds: nextIds,
        updatedAt: Timestamp.now(),
        updatedBy: userData?.id || '',
      } as any);

      setCreateAuthorizedContactModalOpen(false);
      setCreateAuthorizedContactDraft({ displayName: '', email: '' });
      await loadAll();
      setInfo('Authorized billing contact created.');
    } catch (e: any) {
      setError(e?.message || 'Failed to create authorized contact.');
    } finally {
      setSaving(false);
    }
  }, [
    authorizedContactIds,
    billingProfile,
    createAuthorizedContactDraft.displayName,
    createAuthorizedContactDraft.email,
    loadAll,
    resolvedLocationId,
    resolvedPartnerId,
    userData?.id,
    writeBillingProfile,
  ]);

  if (!resolvedSchoolId && !resolvedInvoiceId) {
    return (
      <div className="billing-page">
        <div className="billing-card">
          <h2>Billing</h2>
          <div className="billing-error">Missing school or invoice context.</div>
        </div>
      </div>
    );
  }

  const isInvoiceDetailRoute = location.pathname.startsWith('/app/admin/invoices/');

  return (
    <div className={`billing-page ${isInvoiceDetailRoute ? 'billing-page--fullscreen' : ''}`}>
      <div className="billing-card">
        <div className="billing-header">
          <button
            className="billing-back"
            onClick={() => resolvedPartnerId ? navigate(`/app/admin/partners/${resolvedPartnerId}?tab=billing`) : navigate('/app/admin', { state: { adminTab: 'invoicing', invoicingSchoolId: resolvedSchoolId } })}
          >
            ← Back
          </button>

          <div className="billing-header-main">
            <div className="billing-title">
              Invoice — {school?.name || 'School'}
            </div>
            <div className="billing-subtitle">
              {programTermLabelFromId(termId)}
            </div>
          </div>

          <div className="billing-header-actions">
            {invoice && !isCanceled ? <button className="btn btn-secondary" disabled={saving || loading} onClick={async () => {
              const snoozedUntil = addDaysYmd(new Date().toISOString().slice(0, 10), 7);
              await updateDoc(doc(db, 'invoices', invoice.id), { remindersSnoozedUntil: snoozedUntil, updatedAt: Timestamp.now(), updatedBy: userData?.id || '' });
              setInvoice((current) => current ? ({ ...current, remindersSnoozedUntil: snoozedUntil }) : current);
              setInfo(`Reminders snoozed until ${snoozedUntil}.`);
            }}>Snooze reminders 7 days</button> : null}
            {!isSent && !isCanceled ? (
              <button
                className="btn btn-danger"
                onClick={deleteDraftInvoice}
                disabled={saving || loading || pdfGenerating}
                title="Delete this invoice draft (only before sending)"
              >
                Delete draft
              </button>
            ) : invoice && !isCanceled ? <button className="btn btn-danger" disabled={saving || loading} onClick={() => setConfirmModal({ title: 'Cancel this invoice?', body: 'This makes the invoice unpayable, revokes its portal links, and releases its billed sessions for a corrected replacement. The original remains in invoice history.', confirmText: 'Cancel invoice', confirmTone: 'danger', onConfirm: async () => {
              setConfirmModal(null);
              await cancelInvoiceRecord();
            } })}>Cancel invoice</button> : null}
          </div>
        </div>

        {error && <div className="billing-error">{error}</div>}
        {info && <div className="billing-info">{info}</div>}
        {!resolvedPartnerId && (
          <div className="billing-error">
            Historical school billing and contacts are read-only. Map this invoice/location to a canonical partner before editing billing, recipients, or creating invoices.
          </div>
        )}
        {isCanceled ? (
          <div className="billing-info" role="status">
            <strong>This invoice was canceled. No payment is requested.</strong> It cannot be edited or resent.
            <div style={{ marginTop: 10 }}>
              {(invoice as any)?.replacedByInvoiceId ? (
                <button className="btn btn-primary" onClick={() => navigate(`/app/admin/invoices/${(invoice as any).replacedByInvoiceId}`)}>Open replacement invoice</button>
              ) : (
                <button className="btn btn-primary" disabled={saving || loading} onClick={() => void createReplacement()}>Create replacement invoice</button>
              )}
            </div>
          </div>
        ) : null}

        <div className="billing-status-row">
          <div className={`billing-status-pill tone-${statusTone}`}>{statusLabel(derivedStatus)}</div>
          <div className="billing-status-text">{statusSentence}</div>
        </div>

        {isReadOnlyRecord ? (
          <div className="review-topbar">
            <div className="review-title">Invoice record</div>
            <div className="review-actions">
              <button className="btn btn-primary" onClick={() => setPdfPreviewOpen(true)} disabled={saving || loading || !invoice?.pdf?.url}>
                Preview invoice
              </button>
              {!isCanceled ? <button className="btn btn-secondary" onClick={() => openSendPreview('resend')} disabled={saving || loading || !(invoice as any)?.sentAt}>
                Resend invoice
              </button> : null}
            </div>
          </div>
        ) : null}

        {isReadOnlyRecord ? (
          <div className="review-grid">
            <div className="review-card">
              <div className="review-card-title">Terms</div>
              <div className="kv">
                <div className="k">Legal name</div><div className="v">{billingProfile.legalName || '—'}</div>
                <div className="k">Payment preference</div><div className="v">{String(billingProfile.paymentPreference || '—').toUpperCase()}</div>
                <div className="k">Net invoice</div><div className="v">{billingProfile.netInvoiceDays ?? '—'} days</div>
                <div className="k">Net payment</div><div className="v">{billingProfile.netPaymentDays ?? '—'} days</div>
                <div className="k">Bill to</div><div className="v">
                  {[
                    billingProfile.billingAddress?.line1,
                    billingProfile.billingAddress?.line2,
                    [billingProfile.billingAddress?.city, billingProfile.billingAddress?.state, billingProfile.billingAddress?.postalCode].filter(Boolean).join(', ')
                  ].filter(Boolean).join(' · ') || '—'}
                </div>
              </div>
              <div className="review-subtitle">Recipients &amp; portal access</div>
              <div className="review-list">
                {authorizedContacts.length ? authorizedContacts.map(c => (
                  <div key={c.id} className="review-list-row">
                    <span className="pill">contact</span>
                    <span>{c.displayName || c.id}</span>
                    <span className="muted">{(c as any).email || 'no email'}</span>
                  </div>
                )) : <div className="muted">—</div>}
              </div>
            </div>

            <div className="review-card">
              <div className="review-card-title">Invoice data</div>
              <div className="review-total">
                <div className="review-total-label">Total amount due</div>
                <div className="review-total-value">{fmtUsd(invoice?.totalCents || invoice?.subtotalCents || 0)}</div>
              </div>
              <div className="kv">
                <div className="k">Invoice #</div><div className="v">{invoice?.internalInvoiceNumberSnapshot || invoice?.id || '—'}</div>
                <div className="k">Invoice date</div><div className="v">{fmtYmdMdY(invoice?.invoiceDate)}</div>
                <div className="k">Payment due date</div><div className="v">{fmtYmdMdY(invoice?.paymentDueDate)}</div>
                <div className="k">Service</div><div className="v">
                  {invoice?.serviceDatesSnapshot?.startDate && invoice?.serviceDatesSnapshot?.endDate
                    ? `${fmtYmdMdY(invoice.serviceDatesSnapshot.startDate)} to ${fmtYmdMdY(invoice.serviceDatesSnapshot.endDate)}`
                    : '—'}
                </div>
              </div>
              <div className="review-subtitle">Line items</div>
              <div className="review-list">
                {(invoice?.lineItems || []).map((li, idx) => (
                  <div key={idx} className="review-list-row">
                    <span className="pill">{li.paymentMode === 'school_enrollment' ? 'Partner enrollment' : li.paymentMode === 'school_flat_fee' ? 'Partner flat fee' : 'Other'}</span>
                    <span>{li.description || '—'}</span>
                    <span className="muted">{fmtUsd(li.amountCents || 0)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
        <>
        <div className="billing-section billing-terms-accordion">
          <div className="billing-terms-accordion-header">
            <button
              type="button"
              className="billing-terms-accordion-toggle"
              onClick={() => setTermsAccordionOpen(v => !v)}
              aria-expanded={termsAccordionOpen}
            >
              <span className="billing-terms-accordion-caret">{termsAccordionOpen ? '▾' : '▸'}</span>
              Billing profile
            </button>
            {termsAccordionOpen ? (
              <button
                className="btn btn-primary"
                onClick={saveBillingProfile}
                disabled={saving || loading || !termsDirty || !resolvedPartnerId}
                title={!resolvedPartnerId ? 'Historical billing is read-only until mapped to a partner.' : (!termsDirty ? 'No changes to save' : 'Save term changes')}
              >
                {saving ? 'Saving…' : (!termsDirty ? 'Saved ✓' : 'Save terms')}
              </button>
            ) : null}
          </div>
          {termsAccordionOpen && (
          <>
          <div className="grid-4 grid-4--billing-timing">
            <div className="field">
              <label>Legal name</label>
              <input
                value={billingProfile.legalName || ''}
                onChange={(e) => setBillingProfile(p => ({ ...p, legalName: e.target.value }))}
                disabled={saving || loading}
              />
            </div>
            <div className="field">
              <label>Payment preference</label>
              <select
                className="select-like"
                value={billingProfile.paymentPreference || 'ach'}
                onChange={(e) => setBillingProfile(p => ({ ...p, paymentPreference: e.target.value as any }))}
                disabled={saving || loading}
              >
                <option value="ach">ACH</option>
                <option value="check">Check</option>
                <option value="card">Card</option>
                <option value="portal">Portal</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="field field-narrow">
              <label>Net days until invoice</label>
              <input
                type="number"
                value={billingProfile.netInvoiceDays ?? ''}
                onChange={(e) => setBillingProfile(p => ({ ...p, netInvoiceDays: e.target.value === '' ? undefined : Number(e.target.value) }))}
                disabled={saving || loading}
              />
            </div>
            <div className="field field-narrow">
              <label>Net days until payment</label>
              <input
                type="number"
                value={billingProfile.netPaymentDays ?? ''}
                onChange={(e) => setBillingProfile(p => ({ ...p, netPaymentDays: e.target.value === '' ? undefined : Number(e.target.value) }))}
                disabled={saving || loading}
              />
            </div>
          </div>

          <div className="grid-2">
            <div className="field">
              <label>Billing address (line 1)</label>
              <input
                value={billingProfile.billingAddress?.line1 || ''}
                onChange={(e) => setBillingProfile(p => ({ ...p, billingAddress: { ...(p.billingAddress || {}), line1: e.target.value } }))}
                disabled={saving || loading}
              />
            </div>
            <div className="field">
              <label>Billing address (city/state/zip)</label>
              <input
                value={[
                  billingProfile.billingAddress?.city || '',
                  billingProfile.billingAddress?.state || '',
                  billingProfile.billingAddress?.postalCode || '',
                ].filter(Boolean).join(', ')}
                onChange={(e) => {
                  // lightweight parsing: leave as freeform in city for now
                  setBillingProfile(p => ({ ...p, billingAddress: { ...(p.billingAddress || {}), city: e.target.value } }));
                }}
                disabled={saving || loading}
              />
            </div>
          </div>

          <div className="mini-table">
            <div className="mini-table-title mini-table-title-row">
              <span className="mini-table-title-inline">Authorized contacts</span>
              <button
                type="button"
                className="mini-btn"
                style={{ visibility: 'hidden' }}
                tabIndex={-1}
                aria-hidden="true"
              />
            </div>

            <div style={{ padding: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 160px', gap: 10, alignItems: 'center' }}>
                <SearchableSelect
                  valueId={authorizedContactPickId}
                  query={authorizedContactQuery}
                  placeholder="Search school contacts…"
                  options={contactOptions}
                  disabled={saving || loading}
                  onQueryChange={setAuthorizedContactQuery}
                  onSelectId={(id) => {
                    setAuthorizedContactPickId(id);
                    const opt = contactOptions.find(o => o.id === id);
                    setAuthorizedContactQuery(opt ? opt.label : '');
                  }}
                  onResetQueryToValue={() => {
                    const opt = contactOptions.find(o => o.id === authorizedContactPickId);
                    setAuthorizedContactQuery(opt ? opt.label : '');
                  }}
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => void addAuthorizedContact()}
                  disabled={saving || loading || !resolvedPartnerId || !authorizedContactPickId}
                >
                  Add
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={openCreateAuthorizedContact}
                  disabled={saving || loading || !resolvedPartnerId}
                  title={!resolvedPartnerId ? 'Historical contacts are read-only until mapped to a partner.' : 'Create a new finance contact and authorize it for invoicing'}
                >
                  New contact
                </button>
              </div>

              <div style={{ marginTop: 12 }}>
                {authorizedContacts.length === 0 ? (
                  <div className="muted">No authorized contacts yet.</div>
                ) : (
                  <div className="review-list">
                    {authorizedContacts.map(c => (
                      <div key={c.id} className="review-list-row" style={{ justifyContent: 'space-between' }}>
                        <span>
                          <strong>{c.displayName || c.id}</strong>
                        {String(c.id) === String(billingContact?.id || '') ? (
                          <span className="pill" style={{ marginLeft: 8 }}>Billing contact</span>
                        ) : null}
                          <span className="muted" style={{ marginLeft: 8 }}>{(c as any).email || 'no email'}</span>
                        </span>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button
                          type="button"
                          className="mini-btn"
                          disabled
                          title="Edit canonical contacts from Partner details."
                        >
                          Edit
                        </button>
                        {String(c.id) !== String(billingContact?.id || '') ? (
                          <button
                            type="button"
                            className="mini-btn"
                            onClick={() => void setPrimaryBillingContact(c.id)}
                            disabled={saving || loading || !resolvedPartnerId}
                            title="Set as billing contact"
                          >
                            Make billing contact
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="mini-btn danger"
                          onClick={() => void removeAuthorizedContact(c.id)}
                          disabled={saving || loading || !resolvedPartnerId}
                        >
                          Remove
                        </button>
                      </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
                Stored in the billing profile as <code>authorizedContactIds</code> and <code>billingContactId</code>.
              </div>
            </div>
          </div>
          </>
          )}
        </div>

        <div className="billing-section">
          {!invoice ? (
            <button onClick={ensureInvoice} disabled={saving || loading}>Initialize invoice</button>
          ) : (
            <>
              {enrollmentWarnings.length > 0 ? (
                <section className="billing-enrollment-warnings" role="alert" aria-label="Enrollment warnings">
                  <div className="billing-enrollment-warnings__heading">
                    <strong>Check enrollment before sending</strong>
                    <span>{enrollmentWarnings.length} {enrollmentWarnings.length === 1 ? 'warning' : 'warnings'}</span>
                  </div>
                  <ul>
                    {enrollmentWarnings.map((warning) => (
                      <li key={`${warning.code}-${warning.squadId}`}>
                        <b>{warning.code.replace(/_/g, ' ')}</b>
                        <span>{warning.text}</span>
                      </li>
                    ))}
                  </ul>
                  <label>
                    <input
                      type="checkbox"
                      checked={enrollmentWarningsAcknowledged}
                      onChange={(event) => setEnrollmentWarningsAcknowledged(event.target.checked)}
                    />
                    I checked these headcounts and approve using them on this invoice.
                  </label>
                </section>
              ) : null}
              <div className="billing-squad-picker">
                <div className="billing-squad-picker-header">
                  <div className="billing-squad-picker-title">Select squads for this invoice</div>
                  <div className="billing-squad-picker-actions">
                    {autoSaveStatus ? (
                      <div className={`autosave-indicator tone-${autoSaveStatus.tone}`}>
                        {autoSaveStatus.text}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      className="mini-btn"
                      onClick={() => applySquadSelection([])}
                      disabled={saving || loading || (selectedSquadIds || []).length === 0}
                      title="Clear squad selection"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="billing-squad-picker-controls">
                  <input
                    className="billing-squad-search"
                    placeholder="Search squads by name or id…"
                    value={squadSearch}
                    onChange={(e) => setSquadSearch(e.target.value)}
                    disabled={saving || loading}
                  />
                  <div className="muted">
                    Selected: {(selectedSquadIds || []).length}
                  </div>
                </div>
                {!invoice.sentAt && !['sent', 'paid', 'canceled'].includes(String(invoice.status || '')) && !invoice.legacy && <CustomInvoiceClasses
                  key={invoice.id}
                  invoiceId={invoice.id} squadIds={selectedSquadIds}
                  startDate={invoice.servicePeriodStart || ''} endDate={invoice.servicePeriodEnd || ''}
                  revision={Number((invoice as any).revision || 0)} disabled={saving || loading}
                  onApply={async (data) => {
                    if (autoSaveTimerRef.current) { window.clearTimeout(autoSaveTimerRef.current); autoSaveTimerRef.current = null; }
                    if (invoiceDirty && !(await saveInvoice({ source: 'manual' }))) throw new Error('Save the invoice before applying classes.');
                    setSaving(true);
                    try {
                      await httpsCallable(getFunctions(), 'applyCustomInvoiceClasses')(data);
                      setCoverageDirty(false);
                      await loadAll();
                      setInfo('Classes and pricing updated for this invoice.');
                    } finally { setSaving(false); }
                  }}
                />}
                <div className="billing-squad-list" role="list">
                  {filteredSquads.length ? filteredSquads.map((sq) => {
                    const id = String((sq as any)?.id || '').trim();
                    const checked = (selectedSquadIds || []).includes(id);
                    const start = squadStartYmd(sq);
                    const end = squadEndYmd(sq);
                    const missingDates = !start || !end;
                    const enrolledCount = Array.isArray((sq as any)?.studentIds) ? (sq as any).studentIds.length : 0;
                    const totalClasses = (sq as any)?.totalClasses ?? null;
                    const paymentMode = paymentModeLabel((sq as any)?.paymentMode);
                    const disabled = saving || loading || !id;
                    return (
                      <label
                        key={id || Math.random()}
                        className={`billing-squad-row ${checked ? 'is-selected' : ''} ${missingDates ? 'has-missing-dates' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? Array.from(new Set([...(selectedSquadIds || []), id]))
                              : (selectedSquadIds || []).filter(x => x !== id);
                            applySquadSelection(next);
                          }}
                        />
                        <div className="billing-squad-row-main">
                          <div className="billing-squad-row-title">
                            {String((sq as any)?.title || id || 'Squad')}
                            {missingDates ? <span className="billing-squad-pill warn">Missing dates</span> : null}
                          </div>
                          <div className="billing-squad-row-meta">
                            <span>{start && end ? `${fmtYmdMdY(start)} to ${fmtYmdMdY(end)}` : 'Dates —'}</span>
                            <span>Enrolled: {enrolledCount}</span>
                            {totalClasses != null ? <span>Classes: {String(totalClasses)}</span> : null}
                            <span>Mode: {paymentMode}</span>
                          </div>
                        </div>
                      </label>
                    );
                  }) : (
                    <div className="muted" style={{ padding: 10 }}>No squads found.</div>
                  )}
                </div>
              </div>

              <div className="mini-table">
                <div className="mini-table-title">Line items</div>
                <div className="muted" style={{ marginBottom: 8 }}>
                  Derived from <b>selected squads</b>.
                </div>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 38 }} />
                      <th>Description</th>
                      <th>Squad</th>
                      <th>Dates</th>
                      <th># Classes</th>
                      <th>Type</th>
                      <th>Rate</th>
                      <th>Billing units</th>
                      <th>Calculation / total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(invoice.lineItems || []).map((li, idx) => (
                      <tr key={idx}>
                        <td>
                          <button
                            type="button"
                            className="row-icon-btn"
                            title={(li as any).customSessionPricing ? "Use Choose classes and pricing to change billed sessions" : "Remove line item"}
                            aria-label="Remove line item"
                            onClick={() => setInvoice(inv => {
                              if (!inv) return inv;
                              const arr = [...(inv.lineItems || [])];
                              arr.splice(idx, 1);
                              return { ...inv, lineItems: arr };
                            })}
                            disabled={saving || loading || (li as any).customSessionPricing}
                          >
                            −
                          </button>
                        </td>
                        <td>
                          <span className="invoice-line-field-label">{li.paymentMode === 'school_enrollment' ? 'Tuition per student' : li.paymentMode === 'school_flat_fee' ? 'Flat fee for included classes' : 'Rate'}</span>
                          <input
                            value={li.description}
                            onChange={(e) => setInvoice(inv => {
                              if (!inv) return inv;
                              const arr = [...(inv.lineItems || [])];
                              const cur = arr[idx] as any;
                              let next = { ...cur, description: e.target.value } as any;
                              if (isLinkedLineItem(cur)) next = toManualLineItem(next);
                              arr[idx] = next;
                              return { ...inv, lineItems: arr };
                            })}
                            disabled={saving || loading}
                          />
                        </td>
                        <td>
                          {isLinkedLineItem(li) ? (
                            li.squadTitle || '—'
                          ) : (
                            <select
                              value={String((li as any).squadId || '')}
                              onChange={(e) => setInvoice(inv => {
                                if (!inv) return inv;
                                const arr = [...(inv.lineItems || [])];
                                const cur = arr[idx] as any;
                                const selectedId = String(e.target.value || '').trim();
                                const sq = selectedId ? squadsById.get(selectedId) : undefined;
                                const next: any = { ...cur, manual: true };
                                if (!selectedId) {
                                  delete next.squadId;
                                  delete next.squadTitle;
                                  delete next.manualSourceSquadId;
                                } else {
                                  next.squadId = selectedId;
                                  next.manualSourceSquadId = selectedId;
                                  next.squadTitle = String((sq as any)?.title || '').trim() || next.squadTitle;
                                  // Helpful defaults; still editable after set.
                                  const sd = String((sq as any)?.schedule?.mainDate || (sq as any)?.schedule?.startDate || '').trim();
                                  const ed = String((sq as any)?.schedule?.mainDate || (sq as any)?.schedule?.endDate || '').trim();
                                  if (sd) next.serviceStartDate = sd;
                                  if (ed) next.serviceEndDate = ed;
                                  const tc = Number((sq as any)?.totalClasses);
                                  if (Number.isFinite(tc) && tc > 0) next.classesCount = tc;
                                  const pm = String((sq as any)?.paymentMode || '').trim().toLowerCase();
                                  if (pm === 'school_enrollment' || pm === 'school_flat_fee' || pm === 'stripe') {
                                    next.paymentMode = pm;
                                  }
                                }
                                arr[idx] = next;
                                return { ...inv, lineItems: arr };
                              })}
                              disabled={saving || loading}
                            >
                              <option value="">—</option>
                              {filteredSquads.map((sq) => (
                                <option key={(sq as any).id} value={(sq as any).id}>
                                  {String((sq as any).title || (sq as any).id || '').trim()}
                                </option>
                              ))}
                            </select>
                          )}
                        </td>
                        <td>
                          {isLinkedLineItem(li) ? (
                            li.serviceStartDate && li.serviceEndDate ? `${fmtYmdMdY(li.serviceStartDate)} to ${fmtYmdMdY(li.serviceEndDate)}` : '—'
                          ) : (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <input
                                type="date"
                                value={String((li as any).serviceStartDate || '')}
                                onChange={(e) => setInvoice(inv => {
                                  if (!inv) return inv;
                                  const arr = [...(inv.lineItems || [])];
                                  const cur = arr[idx] as any;
                                  arr[idx] = { ...cur, manual: true, serviceStartDate: safeIsoDate(e.target.value) };
                                  return { ...inv, lineItems: arr };
                                })}
                                disabled={saving || loading}
                              />
                              <span className="muted">to</span>
                              <input
                                type="date"
                                value={String((li as any).serviceEndDate || '')}
                                onChange={(e) => setInvoice(inv => {
                                  if (!inv) return inv;
                                  const arr = [...(inv.lineItems || [])];
                                  const cur = arr[idx] as any;
                                  arr[idx] = { ...cur, manual: true, serviceEndDate: safeIsoDate(e.target.value) };
                                  return { ...inv, lineItems: arr };
                                })}
                                disabled={saving || loading}
                              />
                            </div>
                          )}
                        </td>
                        <td>
                          {isLinkedLineItem(li) ? (
                            (typeof li.classesCount === 'number' ? li.classesCount : '—')
                          ) : (
                            <input
                              type="number"
                              min={0}
                              step={1}
                              value={Number.isFinite(Number((li as any).classesCount)) ? Number((li as any).classesCount) : ''}
                              onChange={(e) => setInvoice(inv => {
                                if (!inv) return inv;
                                const arr = [...(inv.lineItems || [])];
                                const cur = arr[idx] as any;
                                const v = Number(e.target.value);
                                const next: any = { ...cur, manual: true };
                                if (Number.isFinite(v)) next.classesCount = Math.max(0, Math.trunc(v));
                                else delete next.classesCount;
                                arr[idx] = next;
                                return { ...inv, lineItems: arr };
                              })}
                              disabled={saving || loading}
                              style={{ width: 90 }}
                            />
                          )}
                        </td>
                        <td>
                          {isLinkedLineItem(li) ? (
                            li.paymentMode === 'school_enrollment'
                              ? 'Partner enrollment'
                              : li.paymentMode === 'school_flat_fee'
                                ? 'Partner flat fee'
                                : li.paymentMode === 'stripe'
                                  ? 'Stripe'
                                  : '—'
                          ) : (
                            <select
                              value={String((li as any).paymentMode || '')}
                              onChange={(e) => setInvoice(inv => {
                                if (!inv) return inv;
                                const arr = [...(inv.lineItems || [])];
                                const cur = arr[idx] as any;
                                const v = String(e.target.value || '').trim();
                                const next: any = { ...cur, manual: true };
                                if (!v) delete next.paymentMode;
                                else next.paymentMode = v;
                                arr[idx] = next;
                                return { ...inv, lineItems: arr };
                              })}
                              disabled={saving || loading}
                            >
                              <option value="">—</option>
                              <option value="school_enrollment">Partner enrollment</option>
                              <option value="school_flat_fee">Partner flat fee</option>
                              <option value="stripe">Stripe</option>
                            </select>
                          )}
                        </td>
                        <td>
                          <span className="invoice-line-field-label">{Array.isArray((li as any).classDates) ? (li.paymentMode === 'school_enrollment' ? '$ / student / class' : '$ / class') : '$ / billing unit'}</span>
                          <input
                            type="number"
                            step="0.01"
                            value={Number.isFinite(Number(li.rateCents)) ? (Number(li.rateCents) / 100).toFixed(2) : '0.00'}
                            onChange={(e) => setInvoice(inv => {
                              if (!inv) return inv;
                              const arr = [...(inv.lineItems || [])];
                              const cur = arr[idx] as any;
                              const dollars = Number(e.target.value);
                              let next = {
                                ...cur,
                                rateCents: Number.isFinite(dollars) ? Math.max(0, Math.round(dollars * 100)) : 0,
                              } as any;
                              if (isLinkedLineItem(cur)) next = toManualLineItem(next);
                              arr[idx] = next;
                              return { ...inv, lineItems: arr };
                            })}
                            disabled={saving || loading}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={(isLinkedLineItem(li) && li.paymentMode === 'school_flat_fee') ? 1 : li.quantity}
                            onChange={(e) => setInvoice(inv => {
                              if (!inv) return inv;
                              const arr = [...(inv.lineItems || [])];
                              const cur = arr[idx] as any;
                              let next = {
                                ...cur,
                                billedHeadcount: Math.max(0, Math.trunc(Number(e.target.value) || 0)),
                                // Flat fee is always quantity 1
                                quantity: (isLinkedLineItem(cur) && (cur as any).paymentMode === 'school_flat_fee') ? 1 : Math.max(0, Math.trunc(Number(e.target.value) || 0)),
                              } as any;
                              if (isLinkedLineItem(cur)) next = toManualLineItem(next);
                              arr[idx] = next;
                              return { ...inv, lineItems: arr };
                            })}
                            disabled={saving || loading || (isLinkedLineItem(li) && li.paymentMode === 'school_flat_fee')}
                          />
                        </td>
                        <td><strong>{fmtUsd(computeLineItemAmount(li))}</strong><span className="invoice-line-calculation">{!Array.isArray((li as any).classDates) ? `${fmtUsd(li.rateCents)} × ${li.quantity} billing units` : li.paymentMode === 'school_enrollment' ? `${fmtUsd(li.rateCents)} / student / class × ${(li as any).billedHeadcount ?? li.quantity} students × ${Array.isArray((li as any).classDates) ? (li as any).classDates.length : ((li as any).classesCount || 1)} classes` : li.paymentMode === 'school_flat_fee' ? `${fmtUsd(li.rateCents)} / class × ${Array.isArray((li as any).classDates) ? (li as any).classDates.length : ((li as any).classesCount || 1)} classes` : `${fmtUsd(li.rateCents)} × ${li.quantity} units`}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="invoice-lineitems-footer">
                  <div className="invoice-add-line-row">
                    <button
                      type="button"
                      className="row-icon-btn row-icon-btn--add"
                      onClick={() => setInvoice(inv => inv ? ({ ...inv, lineItems: [...(inv.lineItems || []), { description: 'Service', rateCents: 0, quantity: 0, amountCents: 0, manual: true }] }) : inv)}
                      disabled={saving || loading}
                      aria-label="Add manual line item"
                      title="Add manual line item"
                    >
                      +
                    </button>
                    <span className="invoice-add-line-label">Add manual line</span>
                  </div>

                  <div className="invoice-dates-row">
                    <div className="field field-date-inline">
                      <label>Invoice date</label>
                      <div className="date-input-wrap">
                        <input
                          ref={invoiceDateInputRef}
                          type="date"
                          value={invoice.invoiceDate || ''}
                          onChange={(e) => setInvoice(inv => inv ? ({ ...inv, invoiceDate: safeIsoDate(e.target.value), invoiceDateSource: 'manual_override' }) : inv)}
                          disabled={saving || loading}
                        />
                        <button
                          type="button"
                          className="date-input-icon"
                          onClick={() => {
                            const el = invoiceDateInputRef.current as any;
                            if (!el) return;
                            try {
                              if (typeof el.showPicker === 'function') el.showPicker();
                              else el.focus();
                            } catch {
                              el.focus();
                            }
                          }}
                          disabled={saving || loading}
                          aria-label="Pick invoice date"
                          title="Pick invoice date"
                        >
                          📅
                        </button>
                      </div>
                    </div>

                    <div className="field field-date-inline">
                      <label>Payment due date</label>
                      <div className="date-input-wrap">
                        <input
                          ref={dueDateInputRef}
                          type="date"
                          value={invoice.paymentDueDate || ''}
                          onChange={(e) => setInvoice(inv => inv ? ({ ...inv, paymentDueDate: safeIsoDate(e.target.value), paymentTermsSource: 'manual_override', paymentDueDateSource: 'manual_override' }) : inv)}
                          disabled={saving || loading}
                        />
                        <button
                          type="button"
                          className="date-input-icon"
                          onClick={() => {
                            const el = dueDateInputRef.current as any;
                            if (!el) return;
                            try {
                              if (typeof el.showPicker === 'function') el.showPicker();
                              else el.focus();
                            } catch {
                              el.focus();
                            }
                          }}
                          disabled={saving || loading}
                          aria-label="Pick payment due date"
                          title="Pick payment due date"
                        >
                          📅
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="billing-send">
                <button
                  className="btn btn-primary"
                  onClick={handleSendInvoiceClick}
                  disabled={saving || loading || pdfGenerating || (enrollmentWarnings.length > 0 && !enrollmentWarningsAcknowledged)}
                  title={enrollmentWarnings.length > 0 && !enrollmentWarningsAcknowledged ? 'Acknowledge the enrollment warnings first' : 'Preview then send invoice'}
                >
                  {pdfGenerating ? (
                    <span className="btn-loading">
                      <span className="spinner" aria-hidden="true" />
                      Generating PDF{pdfGenElapsedSeconds ? ` (${pdfGenElapsedSeconds}s)` : ''}…
                    </span>
                  ) : (
                    'Send invoice'
                  )}
                </button>
              </div>
            </>
          )}
        </div>
        </>
        )}

        {isReadOnlyRecord ? (
          <div className="review-audit">
            <div className="review-audit-card">
              <div className="review-card-title">Payment confirmations</div>
              <div className="kv">
                <div className="k">Submitted</div>
                <div className="v">
                  {paymentSubmissions.length ? `Yes (${paymentSubmissions.length})` : 'No'}
                  {paymentSubmissions[0]?.submittedAt ? ` • latest ${fmtTimestamp(paymentSubmissions[0].submittedAt)}` : ''}
                </div>
              </div>
              {paymentSubmissions.length ? (
                <div className="review-list" style={{ marginTop: 10 }}>
                  {paymentSubmissions.slice(0, 5).map((p) => (
                    <div key={p.id} className="review-list-row">
                      <span className="pill">{p.method}</span>
                      <span>{p.submittedByEmail || '—'}</span>
                      <span className="muted">{fmtTimestamp((p as any).submittedAt)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="muted" style={{ marginTop: 8 }}>No payment confirmation submitted via the portal yet.</div>
              )}
            </div>

            <div className="review-audit-card">
              <div className="review-card-title">Portal access (magic links)</div>
              <div className="kv">
                <div className="k">Total accesses</div>
                <div className="v">{(invoice as any)?.portalAccessCount ?? 0}</div>
              </div>
              <div className="review-list" style={{ marginTop: 10 }}>
                {portalTokens.length ? portalTokens.map((t) => (
                  <div key={t.id} className="review-list-row">
                    <span className="pill">{t.recipientEmail}</span>
                    <span className="muted">
                      {t.revokedAt ? 'revoked' : 'active'} • {t.accessCount ?? 0} visits
                      {t.lastAccessedAt ? ` • last ${fmtTimestamp(t.lastAccessedAt)}` : ''}
                    </span>
                  </div>
                )) : (
                  <div className="muted">No portal links have been created yet.</div>
                )}
              </div>

              <div className="review-danger-row">
                <div>
                  <div className="review-danger-label">Access</div>
                  <div className="muted">Revokes all portal access links for this invoice.</div>
                </div>
                <button
                  className="btn btn-danger"
                  onClick={revokeLinks}
                  disabled={saving || loading || portalTokens.length === 0}
                  title="Revokes all portal access links for this invoice."
                >
                  Revoke links
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {sendPreviewOpen ? (
        <div className="pdf-modal-overlay" onClick={() => setSendPreviewOpen(null)} role="presentation">
          <div className="pdf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pdf-modal-header">
              <div className="pdf-modal-title">
                {sendPreviewOpen === 'send' ? 'Preview invoice before sending' : 'Preview invoice before resending'}
              </div>
              <button className="pdf-modal-close" onClick={() => setSendPreviewOpen(null)} aria-label="Close preview">✕</button>
            </div>
            <div className="pdf-modal-body">
              <div className="send-preview-meta">
                <div className="muted">
                To: {toEmail || '—'}
                {ccAuthorizedEmails.length ? (
                  <span> • CC: {ccAuthorizedEmails.join(', ')}</span>
                ) : null}
                </div>
                {sendPreviewOpen === 'resend' ? (
                  <div className="muted" style={{ marginTop: 10 }}>
                    <div style={{ fontWeight: 800, color: '#374151' }}>Email message preview</div>
                    <div style={{ marginTop: 6 }}><b>Subject:</b> {resendEmailPreview?.subject || '—'}</div>
                    <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{resendEmailPreview?.body || '—'}</div>
                  </div>
                ) : null}
              </div>
              {sendActionError ? (
                <div className="billing-error" role="alert" style={{ margin: '12px' }}>
                  <b>Invoice not sent.</b> {sendActionError}
                </div>
              ) : null}
              {sendPreviewOpen === 'send' && pdfGenerating ? (
                <div className="pdf-generating-screen">
                  <span className="spinner spinner-lg" aria-hidden="true" />
                  <div className="pdf-generating-text">
                    Generating invoice{pdfGenElapsedSeconds ? ` (${pdfGenElapsedSeconds}s)` : ''}…
                  </div>
                </div>
              ) : sendPreviewOpen === 'send' && sendPreviewError ? (
                <div className="pdf-generating-screen" role="alert">
                  <div className="pdf-generating-text">Invoice generation failed</div>
                  <div className="muted" style={{ marginTop: 8 }}>{sendPreviewError}</div>
                  <div style={{ marginTop: 12 }}>
                    <button className="btn btn-secondary" onClick={() => void generatePdf({ openPreview: false })} disabled={saving}>
                      Retry PDF generation
                    </button>
                  </div>
                </div>
              ) : invoice?.pdf?.url && (!sendPreviewPdfUrlAtOpen || invoice.pdf.url !== sendPreviewPdfUrlAtOpen || sendPreviewOpen === 'resend') ? (
                <iframe className="pdf-frame" title="Invoice PDF" src={invoice.pdf.url} />
              ) : (
                <div className="pdf-generating-screen">
                  <span className="spinner spinner-lg" aria-hidden="true" />
                  <div className="pdf-generating-text">
                    Generating invoice{pdfGenElapsedSeconds ? ` (${pdfGenElapsedSeconds}s)` : ''}…
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <button className="btn btn-secondary" onClick={() => generatePdf({ openPreview: false })} disabled={saving}>
                      Retry PDF generation
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="send-preview-footer">
              <button className="btn btn-secondary" onClick={() => setSendPreviewOpen(null)} disabled={saving}>
                Cancel
              </button>
              {!toEmail ? (
                <button
                  className="btn btn-confirm"
                  onClick={() => {
                    setSendPreviewOpen(null);
                    // No authorized contacts yet; user must add school contacts first.
                  }}
                  disabled={saving || loading}
                  title="Set a billing contact with an email before sending"
                >
                  Set billing contact
                </button>
              ) : (
                <button
                  className="btn btn-confirm"
                  onClick={confirmSendFromPreview}
                  disabled={
                    saving
                    || pdfGenerating
                    || Boolean(sendPreviewError)
                    || !invoice?.pdf?.url
                    || Boolean(sendPreviewOpen === 'send' && sendPreviewPdfUrlAtOpen && invoice?.pdf?.url === sendPreviewPdfUrlAtOpen)
                  }
                  title={
                    pdfGenerating
                    || Boolean(sendPreviewError)
                    || !invoice?.pdf?.url
                    || Boolean(sendPreviewOpen === 'send' && sendPreviewPdfUrlAtOpen && invoice?.pdf?.url === sendPreviewPdfUrlAtOpen)
                      ? 'Generating invoice PDF…'
                      : undefined
                  }
                >
                  {sendPreviewOpen === 'send' ? 'Correct. Send invoice.' : 'Correct. Resend invoice.'}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {createAuthorizedContactModalOpen ? (
        <div className="ui-modal-overlay" onClick={() => setCreateAuthorizedContactModalOpen(false)} role="presentation">
          <div className="ui-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ui-modal-header">
              <div className="ui-modal-title">New authorized billing contact</div>
              <button className="ui-modal-close" onClick={() => setCreateAuthorizedContactModalOpen(false)} aria-label="Close">✕</button>
            </div>
            <div className="ui-modal-body">
              <div className="field">
                <label>Name</label>
                <input
                  value={createAuthorizedContactDraft.displayName}
                  onChange={(e) => setCreateAuthorizedContactDraft(d => ({ ...d, displayName: e.target.value }))}
                  placeholder="Jane Doe"
                  disabled={saving}
                  autoFocus
                />
              </div>
              <div className="field" style={{ marginTop: 10 }}>
                <label>Email</label>
                <input
                  value={createAuthorizedContactDraft.email}
                  onChange={(e) => setCreateAuthorizedContactDraft(d => ({ ...d, email: e.target.value }))}
                  placeholder="name@school.org"
                  disabled={saving}
                />
                <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
                  This creates a <code>finance</code> contact and authorizes it for invoice delivery.
                </div>
              </div>
            </div>
            <div className="ui-modal-footer">
              <button className="btn btn-secondary" onClick={() => setCreateAuthorizedContactModalOpen(false)} disabled={saving}>Cancel</button>
              <button className="btn btn-confirm" onClick={confirmCreateAuthorizedContact} disabled={saving}>Create &amp; authorize</button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmModal ? (
        <div className="ui-modal-overlay" onClick={() => setConfirmModal(null)} role="presentation">
          <div className="ui-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ui-modal-header">
              <div className="ui-modal-title">{confirmModal.title}</div>
              <button className="ui-modal-close" onClick={() => setConfirmModal(null)} aria-label="Close">✕</button>
            </div>
            <div className="ui-modal-body">
              {confirmModal.body}
            </div>
            <div className="ui-modal-footer">
              <button className="btn btn-secondary" onClick={() => setConfirmModal(null)} disabled={saving}>Cancel</button>
              <button
                className={confirmModal.confirmTone === 'danger' ? 'btn btn-danger' : 'btn btn-confirm'}
                onClick={confirmModal.onConfirm}
                disabled={saving}
              >
                {confirmModal.confirmText || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {pdfPreviewOpen ? (
        <div className="pdf-modal-overlay" onClick={() => setPdfPreviewOpen(false)} role="presentation">
          <div className="pdf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pdf-modal-header">
              <div className="pdf-modal-title">Invoice PDF preview</div>
              <button className="pdf-modal-close" onClick={() => setPdfPreviewOpen(false)} aria-label="Close preview">✕</button>
            </div>
            {invoice?.pdf?.url ? (
              <iframe className="pdf-frame" title="Invoice PDF" src={invoice.pdf.url} />
            ) : (
              <div className="muted">No PDF yet. Generate or upload a PDF first.</div>
            )}
          </div>
        </div>
      ) : null}

    </div>
  );
}
